import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  AfterViewInit,
  QueryList,
  Renderer2,
  ElementRef,
  ChangeDetectionStrategy,
} from "@angular/core";
import { ViewChildren, ViewChild } from "@angular/core";
import { Input, Output, EventEmitter, SimpleChanges, ChangeDetectorRef } from "@angular/core";
import { Subject, Subscription } from "rxjs";
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import {
  RateSubscriptionPromiseService,
  TraderGroupPromiseLookupService,
  MarkupCriteria,
  CcyPairPromiseLookupService,
  CcyPairLookupService,
  TraderGroupLookupService,
  BenchmarkFixingPrimaryLookupService,
  ClientPromiseLookupService,
  TimezoneLookupService,
  BranchLookupService,
  ClientLookupService,
  SystemConfigPromiseService,
  SysFeatures,
} from "shared/cubiq-services";

// global-services

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TranslateService } from "@ngx-translate/core";
// order-entry services
import { OESettingsService } from "../services/basic/oe-settings.service";
import { OECoordinatorService } from "../services/oe-coordinator.service";
import { OEApiService } from "../services/oe-api.service";
import {
  OEUtilityService,
  PRIVATIZATION_LABEL,
  RESTRICTED_LABEL,
} from "../services/basic/oe-utility.service";
// order-entry  models
import { OEPanelBmarkModel } from "../data/models/oe-panel-bmark.model";
import {
  OEInputLockStateItemModel,
  OELockingProfileTransportModel,
} from "../data/models/oe-locking-profile.model";
import { OECommandEventModel } from "../data/models/oe-command-event.model";
import { OEInputModel } from "../data/models/oe-input.model";
//  order-entry  enums
import { OENInputControl } from "../data/enums/oe-input-control.enum";
import { OENPanel } from "../data/enums/oe-panel.enum";
import { OENLockedState } from "../data/enums/oe-locked-state.enum";
import { OENCommand } from "../data/enums/oe-command.enum";
// interface controls components
import { ICAutocompleteComponent } from "./controls/ic-autocomplete.component";
import { BfxContactsInputComponent } from "./controls/bfx-contacts-input.component";
import { BfxClientIntentToggleComponent } from "./controls/bfx-toggle-client-intent.component";
import { BfxAmountSpinnerComponent } from "./controls/bfx-amount-spinner.component";
import { BfxMarkupComponent } from "../interface/controls/bfx-markup.component";
import {
  BfxAutoCompleteComponent,
  BfxAutoCompleteComponentState,
} from "app/bfx-element-library/bfx-autocomplete.component";
// interface controls enums
import { ICNInputControlState } from "./controls/models/bfx-interface-controls.enums";
// interface controls models
import {
  ICFixedCcyModel,
  ICAutocompleteGenericModel,
  ICMarkupModel,
  ICTenorValueDateModel,
} from "./controls/models/bfx-interface-controls.models";
import { ICHoldSTPComponent } from "./controls/ic-hold-stp.component";
import { ICPrivateOrderComponent } from "./controls/ic-private-order.component";
import { OEDLoadingDataModel } from "../data/models/oe-loading-data.model";
import { OENEntryType } from "../data/enums/oe-entry-type.enum";

import {
  INPUT_TIME_SHOWSECONDS,
  INPUT_TIME_PATTERN,
  SECS_VALIDATE_TIME_PATTERN,
  SECS_MASK,
  INPUT_TIME_MASK,
  INPUT_TIME_DEFAULT,
  SECS_DEFAULT,
} from "./controls/bfx-time-input.component";
import { OERateDisplayModel } from "../data/models/rate/oe-rate-display.model";
import { OERateTransportModel } from "../data/models/rate/oe-rate-transport.model";
import { OERateDataModel } from "../data/models/rate/oe-rate-data.model";
import { OELoggerService } from "../services/basic/oe-logger.service";
import { ICCcyPairStringComponent } from "./controls/ic-ccy-pair-string.component";
import { BfxValueDateComponent } from "./controls/bfx-value-date.component";
import { OETimeMachineService } from "../services/basic/oe-time-machine.service";

import { BfxCalendarComponent } from "./controls/good-from-until/bfx-calendar.component";

import {
  BenchmarkFixingDetail,
  BenchmarkFixingTime,
} from "shared/cubiq-services/lookup/model/impl/BenchmarkFixingPrimaryLookupService";
import { TRADING_SOURCE_INTERNAL } from "shared/cubiq-services/model/TradingSource.constants";
import { OENOperationMode } from "../data/enums/oe-operation-mode.enum";

import { OENLockingProfileType } from "../data/enums/oe-locking-profile-type.enum";
import { ICFixedCcyComponent } from "../../ic-controls/ic-fixed-ccy.component";
import { InputText } from "@bfx/primeng/inputtext";
import { BfxConfigService } from "shared/auth-services/system-config/bfx-config.service";
import { ICCustomFieldsComponent } from "app/order-entry/interface/controls/ic-custom-fields.component";
import { OEDatasetService } from "app/order-entry/services/basic/oe-dataset.service";
import { ICAutoFwdPricingComponent } from "app/order-entry/interface/controls/ic-auto-fwd-pricing.component";
import { OEAdapterForwardService } from "../services/adapter/oe-adapter-forward.service";
import { OEModelGetterService } from "../services/model/oe-model-getter.service";

const BIG_NUMBER_ZERO: CubiqTS.BigNumber = new CubiqTS.BigNumber(0);

@Component({
  selector: "oe-panel-bmark-entry",
  templateUrl: "./html/oe-panel-bmark-entry.html",
  providers: [
    { provide: INPUT_TIME_SHOWSECONDS, useValue: true },
    { provide: INPUT_TIME_PATTERN, useValue: SECS_VALIDATE_TIME_PATTERN },
    { provide: INPUT_TIME_MASK, useValue: SECS_MASK },
    { provide: INPUT_TIME_DEFAULT, useValue: SECS_DEFAULT },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OEBmarkEntryComponent implements OnDestroy, OnInit, OnChanges, AfterViewInit {
  @Input() refId: string;
  @Input() rootRefId: string; // refId of the root order
  @Input() entryType: OENEntryType;
  @Input() operationMode: OENOperationMode = null;

  @Input() unTabbableTabs: boolean = true;
  /* Tab Index binding */
  tabIndex: number;
  @Output() tabIndexChange: EventEmitter<number> = new EventEmitter<number>();
  @Output() dataLoadCompleted: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input()
  get tab() {
    return this.tabIndex;
  }
  set tab(value: any) {
    this.tabIndex = value;
    this.tabIndexChange.emit(this.tabIndex);
  }

  public control: {
    showIntentAndAmount: boolean;
    multiplyAmountUserValue: number;
    multiplyAmountUserToggle: boolean;
    commentSize: number;
    lockAllFg: boolean;
    isBulked: boolean;
    isBulking: boolean;
    isLoading: boolean;
    initialLoadingComplete: boolean;
    lockingProfileData: Array<OEInputLockStateItemModel> | null;
    clientAccountConstraintAutoSelect: boolean;
    traderGroupAutocompleteConfig: {
      allowEmpty: boolean;
      forceSelection: boolean;
      customValidation: any;
    };
    fixingTimeAutocompleteConfig: {
      allowEmpty: boolean;
      forceSelection: boolean;
      customValidation: any;
    };
    fixingCodeAutocompleteConfig: {
      allowEmpty: boolean;
      forceSelection: boolean;
      customValidation: any;
    };
  } = {
    showIntentAndAmount: true,
    multiplyAmountUserValue: 0,
    multiplyAmountUserToggle: false,
    commentSize: 250,
    lockAllFg: false,
    isBulked: false,
    isBulking: false,
    isLoading: false,
    initialLoadingComplete: false,
    lockingProfileData: null,
    clientAccountConstraintAutoSelect: true,
    traderGroupAutocompleteConfig: {
      allowEmpty: true,
      forceSelection: true,
      customValidation: null,
    },
    fixingTimeAutocompleteConfig: {
      allowEmpty: false,
      forceSelection: true,
      customValidation: null,
    },
    fixingCodeAutocompleteConfig: {
      allowEmpty: false,
      forceSelection: true,
      customValidation: null,
    },
  };

  public tabData: any = {
    financials: {
      label: "Financials",
      label_key: "order_entry.screen.tabs.label.financials",
    },
    more: {
      label: "More",
      label_key: "order_entry.screen.tabs.label.more",
    },
  };

  public rate = {
    value: null,
  };
  /*
	private previousValue = {
		client: null,  // ClientImpl
		clientId: null, // string
	}; */

  public data: OEPanelBmarkModel = new OEPanelBmarkModel();

  public markupCheckboxEnabled: boolean = true;
  public latestRateData: any = null;

  public convertedUTCTimeFixingList: string[] = [];
  // subscriptions
  private coordinatorSubscription: Subscription;
  private rateSubscription: Subscription;

  private fwdEnabled: boolean = false;
  public oboEnabled: boolean = true;

  public hasCustomFields: boolean = true;

  public _isBulked: boolean = false;

  public get isBulked() {
    return this.control.isBulked;
  }

  public set isBulked(isBulked) {
    this.control.isBulked = isBulked;
    if (this.refId != null) {
      this.api.updateBulked(this.refId, isBulked);
    }
    this.updateRetrieveMarkupEnabled();
  }

  public _isBulking: boolean = false;

  public get isBulking() {
    return this.control.isBulking;
  }

  public set isBulking(isBulking) {
    let loadValueDateDataset = false;

    if (this.control.isBulking == true && isBulking == false) {
      loadValueDateDataset = true;
    }

    this.control.isBulking = isBulking;
    if (this.refId != null) {
      this.api.updateBulking(this.refId, isBulking);
    }

    this.updateRetrieveMarkupEnabled();

    this.cd.markForCheck();

    if (loadValueDateDataset) {
      this.loadValueDateDataset();
    }
  }

  /**
   * Fixing Loaded
   */
  loadingControls: boolean = false;
  loadingFixings: boolean = false;
  pendingFixingDate: any = null;
  pendingFixingTime: string = null;
  pendingFixingTimezone: string = null;
  clientBranch: string = null;
  allocationGridLoadSubject = new Subject<any>();

  /**********************************************************************/
  @ViewChild("tabs", { static: true }) elTabs: any;

  @ViewChildren("inputClient") icClient: QueryList<ICAutocompleteComponent>;
  @ViewChildren("inputAccount") icAccount: QueryList<ICAutocompleteComponent>;

  @ViewChildren("inputContacts")
  contactsInputControl: QueryList<BfxContactsInputComponent>;
  @ViewChildren("inputClientTrader")
  icClientTrader: QueryList<ICAutocompleteComponent>;

  @ViewChildren("inputCcyPair") icCcyPair: QueryList<ICCcyPairStringComponent>;
  @ViewChildren("inputOrderType")
  icOrderType: QueryList<ICAutocompleteComponent>;

  @ViewChildren("toggleClientIntent")
  clientIntentToggleControl: QueryList<BfxClientIntentToggleComponent>;
  @ViewChildren("toggleFixedCcy")
  fixedCcyControl: QueryList<ICFixedCcyComponent>;
  @ViewChildren("inputOrderAmount")
  orderAmountInputControl: QueryList<BfxAmountSpinnerComponent>;

  @ViewChildren("inputMarkup")
  markupInputControl: QueryList<BfxMarkupComponent>;
  @ViewChildren("inputValueDate")
  valueDateInputControl: QueryList<BfxValueDateComponent>;

  @ViewChildren("inputFixingCode")
  fixingCodeControl: QueryList<BfxAutoCompleteComponent>;
  @ViewChildren("inputFixing")
  fixingControl: QueryList<BfxAutoCompleteComponent>;

  @ViewChildren("inputTraderGroup")
  traderGroupInputControl: QueryList<BfxAutoCompleteComponent>;
  @ViewChildren("inputHoldSTP") icHoldSTP: QueryList<ICHoldSTPComponent>;
  @ViewChild("autoFwdPricing") autoFwdPricing: ICAutoFwdPricingComponent;
  @ViewChildren("inputPrivateOrder")
  icPrivateOrder: QueryList<ICPrivateOrderComponent>;

  @ViewChild("fixingDate") calendar: BfxCalendarComponent;
  @ViewChild("inputCommentClient") icCommentClient: ElementRef;

  @ViewChildren("oboClient") oboClient: QueryList<InputText>;
  @ViewChildren("oboAccount") oboAccount: QueryList<InputText>;

  @ViewChildren("customFieldComponent") customFields: QueryList<ICCustomFieldsComponent>;

  /**********************************************************************/
  /* CONSTRUCTOR  */
  constructor(
    private translate: TranslateService,
    private settings: OESettingsService,
    private coordinator: OECoordinatorService,
    private traderGroupService: TraderGroupPromiseLookupService,
    private bmFixService: BenchmarkFixingPrimaryLookupService,
    private getter: OEModelGetterService,
    private forwardAdapter: OEAdapterForwardService,
    private utility: OEUtilityService,
    private api: OEApiService,
    public cd: ChangeDetectorRef,
    private _oeTimeMachine: OETimeMachineService,
    private currencyPairService: CcyPairPromiseLookupService,
    protected rateSubscriptionService: RateSubscriptionPromiseService,
    protected oeLogger: OELoggerService,
    public renderer2: Renderer2,
    private sysConfigService: SystemConfigPromiseService,
    protected _clientLookupService: ClientPromiseLookupService,
    protected timeZoneLookupService: TimezoneLookupService,
    protected configService: BfxConfigService,
    protected branchLookupService: BranchLookupService,
    protected clientLookupService: ClientLookupService,
    private datasetService: OEDatasetService,
  ) {
    this.sysConfigService.getSystemConfig(SysFeatures.allowFwdBm).then((value) => {
      this.fwdEnabled = "Y" == value || "y" == value ? true : false;
    });
    configService.getUIConfig().then((config) => {
      this.oboEnabled = config && config.general ? config.general.oboEnabled : false;
    });
    this.oeLogger = oeLogger.for(__dirname);

    this.hasCustomFields = datasetService?.customFieldDefns?.editable?.length > 0;
  }

  /**********************************************************************/
  /* LIFE CYCLE HOOKS */

  ngOnInit() {
    this.initialize();
  }

  ngOnDestroy() {
    this.unsubscribeRate(this.rootRefId, this.data.currencyPair.product);

    if (this.coordinatorSubscription) {
      this.coordinatorSubscription.unsubscribe();
    }
    if (this.rateSubscription) {
      this.rateSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    for (let propName in changes) {
      switch (propName) {
        case "rootRefId":
          if (this.data.previousValue.rootRefId == null) {
            this.data.previousValue.rootRefId = this.rootRefId;
          } else if (
            typeof this.data.previousValue.rootRefId === "string" &&
            this.data.previousValue.rootRefId !== this.rootRefId
          ) {
            // If there is a root change (copy or edit), subscribe new rootRefId and unsubscribe old rootRefId
            setTimeout(() => {
              this.unsubscribeRate(
                this.data.previousValue.rootRefId,
                this.data.currencyPair.product,
              );
              this.subscribeRate(this.rootRefId, this.data.currencyPair.product);
              this.data.previousValue.rootRefId = this.rootRefId;
            }, 50);
          }
          break;
      }
    }
  }

  ngAfterViewInit() {
    if (this.unTabbableTabs) {
      this.makeTabsUntabbable();
    }

    setTimeout(() => {
      this.propagateMarkupDefaultData();
      this.setOrderType();
    }, 200);
  }

  /**********************************************************************/
  /* INITIALIZATION */

  protected initialize(): void {
    this.initializeSettings();
    this.initializeLabelTranslations();
    // create dataset
    this.currencyPairsGetDataset();
    this.initializeTraderGroups();

    // subscriptions
    this.subscribeToCoordinator();
    // amend markup check - for styling
    if (this.operationMode === OENOperationMode.AMEND_MARKUP) {
      this.data.markup.isAmendMarkup = true;
    }
  }

  private initializeSettings(): void {
    // client panel
    let clientSettings = this.settings.getSysConfigForClientPanel();
    this.data.clientTrader.enabled = clientSettings.clientTraderEnabled;
    this.data.contacts.size = clientSettings.contactsRenderSize;

    // financials panel
    let financialsSettings = this.settings.getSysConfigForFinancialsPanel();
    this.data.markup.config.markupCheckboxSelected = financialsSettings.markupBmarkCbx;
    this.data.markup.config.markupEnabled = financialsSettings.markupAllowBmarkEntry;
    this.data.markup.config.markupType = financialsSettings.markupRetrievalTypeBmark;
    //this.data.markup.config.markupLimitDefault = financialsSettings.markupBmarkDefault; // duplicate
    this.data.markup.config.markupRetrievalEnabled = financialsSettings.markupRetrievalTypeBmark;

    this.data.traderGroup.traderGroupModifiable = financialsSettings.traderGroupModifiableBmark;
    this.data.markup.config.markupEntry = financialsSettings.markupAllowBmarkEntry;
    if (this.api.isOperationModeModify(this.refId)) {
      this.data.traderGroup.traderGroupModifiable = financialsSettings.tgModifyBmark;
      this.data.markup.config.markupEntry = financialsSettings.markupModifyBmark;
    }
    // more panel
    let moreSettings = this.settings.getSysConfigForMorePanel();
    this.data.privateOrder.show = this.data.privateOrder.showConfig = moreSettings.showPrivateOrder;
    this.data.commentPrivate.show = this.data.commentPrivate.showConfig =
      moreSettings.showCommentPrivate;
    this.data.autoFwdPricing.enabledConfig = moreSettings.autoForwardPricingEnabled;
    this.data.autoFwdPricing.manualOnly = moreSettings.manualForwardPricingOnlyBMark;

    this.data.holdSTP.enabledConfig = moreSettings.holdSTPisEnabled;
    this.data.commentClient.checkbox.show = moreSettings.showClientCommentCheckbox;
    // Profile settings
    this.control.multiplyAmountUserValue = this.settings.multiplyAmountUserValue;
    this.control.multiplyAmountUserToggle = this.settings.multiplyAmountUserToggle;

    this.data.orderType.value = new ICAutocompleteGenericModel(
      CubiqTS.RequestOrderType.BMARK,
      this.settings.getBmarkDesc(),
    );
    this.data.orderType.dataset = [this.data.orderType.value];
    this.cd.markForCheck();
  }

  private initializeLabelTranslations(): void {
    this.tabData.financials.label = this.translate.instant(this.tabData.financials.label_key);
    this.tabData.more.label = this.translate.instant(this.tabData.more.label_key);
    /********* FINANCIALS PANEL *********/
    this.data.currencyPair.label = this.translate.instant(this.data.currencyPair.labelKey);
    this.data.orderAmount.label = this.translate.instant(this.data.orderAmount.labelKey);
    this.data.clientIntent.label = this.translate.instant(this.data.clientIntent.labelKey);
    this.data.markup.label = this.translate.instant(this.data.markup.labelKey);
    /********* MORE PANEL *********/
    this.data.commentPrivate.label = this.translate.instant(this.data.commentPrivate.labelKey);
    this.data.commentClient.label = this.translate.instant(this.data.commentClient.labelKey);
    this.data.holdSTP.label = this.translate.instant(this.data.holdSTP.labelKey);
    this.data.privateOrder.label = this.translate.instant(this.data.privateOrder.labelKey);
    this.cd.markForCheck();
  }

  protected initializeTraderGroups(): void {
    this.traderGroupService.isPrimed().then(() => {
      this.loadTraderGroups();
    });
  }

  /**
   * Fixing Data is loaded in the Static Data Profile
   * We need to parcel it out into Fixing Codes and then fixings keyed by each code.
   *
   */
  protected async loadFixingData(): Promise<void> {
    await this.bmFixService.isPrimed();
    this.cd.markForCheck();
    let clientBranch =
      this.data.client != null && this.data.client.value instanceof CubiqTS.ClientImpl
        ? this.data.client.value.branchCd
        : this.clientBranch;
    if (
      this.data.currencyPair &&
      this.data.currencyPair.base &&
      this.data.currencyPair.nonBase &&
      this.data.currencyPair.product &&
      clientBranch
    ) {
      let base: string = this.data.currencyPair.inverted
        ? this.data.currencyPair.product.substring(3, 6)
        : this.data.currencyPair.product.substring(0, 3);
      let nonBase: string = this.data.currencyPair.inverted
        ? this.data.currencyPair.product.substring(0, 3)
        : this.data.currencyPair.product.substring(3, 6);
      let bmCodes: Map<string, BenchmarkFixingDetail> =
        await this.bmFixService.getBenchmarkCodesForPairAndBranch(base, nonBase, clientBranch);
      this.data.fixing.allBmarkFixings = [...bmCodes.values()];
      this.data.fixing.allBmarkCodes = [...bmCodes.keys()];
      if (this.control.lockAllFg == false) {
        this.data.fixing.lockState = OENLockedState.UNLOCKED;
        this.data.fixing.locked = false;
      } else {
        this.data.fixing.locked = true;
      }
      this.data.fixing.codeDisabled = false;
      this.data.fixing.disabled = false;
      this.data.fixing.fixingCodeState = ICNInputControlState.INACTIVE;
      this.data.fixing.fixingState = ICNInputControlState.INACTIVE;

      if (this.data.fixing.allBmarkFixings.length > 0) {
        await this.handleFixingCodesChanged();
        this.fixingCodeControl.forEach((c) => c.markForCheck());
        this.fixingControl.forEach((c) => c.markForCheck());
      } else {
        // edge case where we switch to a client/pair without any fixings
        this.data.fixing.zeroAvailableFixings = true;
        this.data.fixing.fixings = [];
        this.data.fixing.fixing = null;
        this.data.fixing.fixingCodes = [];
        this.data.fixing.fixingCode = null;
      }
    } else {
      this.data.fixing.lockState = OENLockedState.LOCKED;
      this.data.fixing.locked = true;
    }

    this.fixingCodeControl.forEach((c) => c.markForCheck());
    this.fixingControl.forEach((c) => c.markForCheck());
    this.updateRetrieveMarkupEnabled();
    this.cd.markForCheck();
  }

  /**
   * If there is only one fixing code - then we select it and disable the control.
   * otherwise we enable the control)
   */
  async handleFixingCodesChanged(): Promise<void> {
    const { selected, singleFixing } = this.loadFixingCodeSuggestions();

    if (selected != null) {
      await this.onFixingCodeChange(selected);
      this.data.fixing.codeDisabled = false;
      this.data.fixing.fixingCodeState = ICNInputControlState.VALID;
    } else if (singleFixing) {
      await this.onFixingCodeChange(this.data.fixing.fixingCodes[0]);
      this.data.fixing.codeDisabled = true;
      this.data.fixing.fixingCodeState = ICNInputControlState.VALID;
    } else {
      this.data.fixing.fixingCodeState = ICNInputControlState.INACTIVE;
      this.data.fixing.codeDisabled = false;
    }
    this.fixingCodeControl.forEach((c) => c.markForCheck());
  }
  /**
   * If there is only one fixing - then we select it and disable the control.
   * otherwise we enable the control)
   */
  handleSingleFixingTimeInCode(code: BenchmarkFixingDetail) {
    if (null != code && 1 == code.times.length) {
      this.onFixingChange(code.times[0], false);
      this.data.fixing.fixingState = ICNInputControlState.VALID;
      this.data.fixing.disabled = true;
    } else {
      this.data.fixing.fixingState = ICNInputControlState.INACTIVE;
      this.data.fixing.disabled = false;
    }
    this.fixingControl.forEach((c) => c.markForCheck());
  }

  protected async getFixingData(): Promise<Array<BenchmarkFixingDetail>> {
    await this.bmFixService.isPrimed();
    let clientBranch =
      this.data.client != null && this.data.client.value instanceof CubiqTS.ClientImpl
        ? this.data.client.value.branchCd
        : this.clientBranch;

    if (
      this.data.currencyPair &&
      this.data.currencyPair.base &&
      this.data.currencyPair.nonBase &&
      this.data.currencyPair.product &&
      clientBranch
    ) {
      let base: string = this.data.currencyPair.inverted
        ? this.data.currencyPair.product.substring(3, 6)
        : this.data.currencyPair.product.substring(0, 3);
      let nonBase: string = this.data.currencyPair.inverted
        ? this.data.currencyPair.product.substring(0, 3)
        : this.data.currencyPair.product.substring(3, 6);
      let bmCodes: Map<string, BenchmarkFixingDetail> =
        await this.bmFixService.getBenchmarkCodesForPairAndBranch(base, nonBase, clientBranch);
      return [...bmCodes.values()];
    } else {
      return [];
    }
  }

  /**
   *
   * @returns previously selected fixing with updated times
   */
  public loadFixingCodeSuggestions(): { selected: BenchmarkFixingDetail; singleFixing: boolean } {
    if (Array.isArray(this.data.fixing.allBmarkFixings)) {
      logger.out.info(
        `${
          this.constructor.name
        }.loadFixingCodeSuggestions() [${this.data.fixing.allBmarkFixings.join(", ")}]`,
      );

      this.data.fixing.fixingCodes = [...this.data.fixing.allBmarkFixings];
    } else {
      this.data.fixing.fixingCodes = [];
    }

    this.cd.markForCheck();

    return {
      selected:
        this.data.fixing.fixingCode != null &&
        typeof this.data.fixing.fixingCode === "object" &&
        Object.hasOwn(this.data.fixing.fixingCode, "code") &&
        this.data.fixing.fixingCodes.find(
          (detail) => detail.code === this.data.fixing.fixingCode.code,
        ),
      singleFixing: this.data.fixing.fixingCodes.length === 1,
    };
  }

  public searchFixingCodes(event = { query: "" }): boolean {
    let query: string = typeof event?.query === "string" ? event.query.toLowerCase() : "";

    if (query.length > 0) {
      this.data.fixing.fixingCodes = this.data.fixing.allBmarkFixings.filter(
        (fixingCode: BenchmarkFixingDetail) => fixingCode.code.toLowerCase().includes(query),
      );
    } else {
      this.data.fixing.fixingCodes = [...this.data.fixing.allBmarkFixings];
    }

    this.cd.markForCheck();

    return this.data.fixing.fixingCodes.length === 1;
  }

  public searchFixings(filter: any): void {
    let query: string = filter.query.toLowerCase();
    let currentFixing: BenchmarkFixingDetail = this.data.fixing.fixingCode;
    if (query.length > 0 && currentFixing != null && Array.isArray(currentFixing.times)) {
      this.data.fixing.fixings = currentFixing.times.filter(
        (fixing: BenchmarkFixingTime) =>
          fixing.label.toLowerCase().startsWith(query) ||
          fixing.label.toLowerCase().endsWith(query, fixing.label.length),
      );
    } else {
      if (currentFixing != null && Array.isArray(currentFixing.times)) {
        this.data.fixing.fixings = [...currentFixing.times];
      } else {
        this.data.fixing.fixings = [];
      }
    }
  }

  /**
   * Loaded
   */
  public selectFixingDate(date): void {
    if (!this.loadingFixings && !this.loadingControls) {
      this.data.fixingDate.value = date;
      this.coordinator.cmdUnbulk(this.refId, OENInputControl.FIXING_DATE);
      this.api.updateFixingDate(this.refId, this.data.fixingDate.value);
      this.data.fixingDate.externalState = null;
      this.data.fixingDate = { ...this.data.fixingDate };
      this.loadValueDateDataset();
      this.pendingFixingDate = null;
    } else {
      this.pendingFixingDate = date;
    }
  }

  /**
   * Loaded
   */
  public selectFixingTime(time): void {
    if (
      !this.loadingFixings &&
      !this.loadingControls &&
      time &&
      Array.isArray(this.data.fixing.fixings) &&
      (this.data.fixing.zeroAvailableFixings || this.data.fixing.fixings.length > 0) &&
      ((this.data.fixing.fixing && this.data.fixing.fixing.timezoneCd) ||
        this.pendingFixingTimezone)
    ) {
      this.data.fixing.fixing = this.findFixingByTimeAndTimezone(
        this.data.fixing.fixings,
        time,
        this.data.fixing.fixing && this.data.fixing.fixing.timezoneCd
          ? this.data.fixing.fixing.timezoneCd
          : this.pendingFixingTimezone,
      );

      // should be invoked when order is entered from FIX or other external channels
      if (this.data.fixing.fixing == null) {
        this.updateUnmatchedFixingTime();
      } else {
        this.selectFixing();
      }
      if (null == this.data.fixing.fixing) {
        logger.out.debug(
          "validateFixing: specified fixing [" +
            time +
            "][" +
            (this.data.fixing.fixingCode ? this.data.fixing.fixingCode.code : null) +
            "] not found",
        );
        this.data.fixing.fixingState = ICNInputControlState.INVALID;
      }
      this.pendingFixingTime = null;
      this.updateRetrieveMarkupEnabled();
    } else {
      this.pendingFixingTime = time;
    }
  }

  private updateUnmatchedFixingTime() {
    let now: Date = new Date();
    let fixingDate: Date =
      this.data.fixingDate && this.data.fixingDate.value ? this.data.fixingDate.value : now;
    let fixingDateString: string =
      fixingDate.getFullYear() +
      "-" +
      ("0" + (fixingDate.getMonth() + 1)).slice(-2) +
      "-" +
      ("0" + fixingDate.getDate()).slice(-2);
    let convertedDateTime = this.convertDateTimeToUTCTime(
      fixingDateString,
      this.pendingFixingTimezone,
      this.pendingFixingTime,
    );
    let doesFixingExist =
      this.data.fixing.fixings.find((fixing: BenchmarkFixingTime) => {
        let convertedUTCTime = this.convertDateTimeToUTCTime(
          fixingDateString,
          fixing.timezoneCd,
          fixing.value,
        );
        this.convertedUTCTimeFixingList.push(convertedUTCTime + " utc"); // added because of autoComplete component validation
        return convertedUTCTime == convertedDateTime;
      }) != null;
    this.data.fixing.fixing = doesFixingExist
      ? new BenchmarkFixingTime(
          new CubiqTS.Long(Number(this.pendingFixingTime)),
          "UTC", // should be either UTC or null
        )
      : null;
    this.data.unmatchedFixingDetails = doesFixingExist ? this.data.fixing.fixing : null;
    this.selectFixing(true);
  }

  private convertDateTimeToUTCTime(
    fixingDateString: string,
    timezoneCd: string,
    fixingTime: string,
  ) {
    let timeZoneImpl: CubiqTS.TimeZoneImpl = this.timeZoneLookupService.getTimezone(timezoneCd);
    if (timeZoneImpl != null && fixingTime != null) {
      let fixingTimeNumber: number = Number(fixingTime);
      fixingDateString +=
        "T" +
        ("0" + (fixingTimeNumber - (fixingTimeNumber % 100)) / 100).slice(-2) +
        ":" +
        ("0" + (fixingTimeNumber % 100)).slice(-2);
      let convertedDateTime: CubiqTS.Moment = CubiqTS.moment
        .tz(fixingDateString, timeZoneImpl.javaTimeZoneCd)
        .utc();
      return convertedDateTime.format("HH:mm");
    }
    return null;
  }

  /**
   * Loaded
   */
  public selectFixingTimezone(timezoneCd): void {
    if (
      !this.loadingFixings &&
      !this.loadingControls &&
      this.data.fixing.fixing &&
      ((this.data.fixing.fixing && this.data.fixing.fixing.value) || this.pendingFixingTime)
    ) {
      if (this.data.fixing.fixing == null) {
        this.data.fixing.fixing = this.findFixingByTimeAndTimezone(
          this.data.fixing.fixings,
          this.data.fixing.fixing && this.data.fixing.fixing.value
            ? this.data.fixing.fixing.value
            : this.pendingFixingTime,
          timezoneCd,
        );
      }
      this.selectFixing();
      this.pendingFixingTimezone = null;
    } else {
      this.pendingFixingTimezone = timezoneCd;
    }
  }

  public findFixingByTimeAndTimezone(
    fixings: BenchmarkFixingTime[],
    time: string,
    timezone: string,
  ): BenchmarkFixingTime {
    let ret: BenchmarkFixingTime = fixings.find(
      (fixingTime: BenchmarkFixingTime) =>
        fixingTime.value == time && fixingTime.timezoneCd == timezone,
    );

    return ret;
  }

  public async loadFixingCodeString(code: string) {
    if (null == code || "" == code) {
      return;
    }
    this.loadingFixings = true;
    let clientBranch =
      this.data.client != null && this.data.client.value instanceof CubiqTS.ClientImpl
        ? this.data.client.value.branchCd
        : this.clientBranch;
    let newFixingCode: BenchmarkFixingDetail = await this.bmFixService.getBenchmarkDetail(
      code,
      this.data.currencyPair.base,
      this.data.currencyPair.nonBase,
      clientBranch,
    );

    if (this.data.client == null || this.data.client.value == null) {
      newFixingCode = null;
    }

    if (null != newFixingCode) {
      this.data.fixing.fixingCode = newFixingCode;

      await this.selectFixingCode(newFixingCode);
      await this.loadPendingControls(true);
      this.loadingFixings = false;
    } else {
      logger.out.debug(
        `${this.constructor.name}.fallbackFixingCodeString() specified benchmark code ` +
          `${code} not found for pair ` +
          `base: ${this.data.currencyPair.base} ` +
          `nonBase:${this.data.currencyPair.nonBase} and branch ${clientBranch}`,
      );
      await this.fallbackFixingCodeString(code);
      this.loadingFixings = false;
    }
  }

  private async fallbackFixingCodeString(code: string) {
    this.data.fixing.fixingCodeState = ICNInputControlState.INVALID;
    this.data.fixing.fixingState = ICNInputControlState.INVALID;
    this.data.fixingDate.state = ICNInputControlState.INVALID;
    this.data.fixing.disabled = true;
    this.data.fixingDate.disabled = true;
    this.fixingCodeControl.forEach((c) => c.markForCheck());
    this.fixingControl.forEach((c) => c.markForCheck());

    if (code != null) {
      const fixingCodes: CubiqTS.BenchmarkFixingImpl[] = await this.bmFixService.getAll();
      let fixing = fixingCodes.find((f) => f.benchmarkCd == code) != null;
      if (fixing != null) {
        this.data.fixing.fixingCode = new BenchmarkFixingDetail(code, null);
        this.data.fixing.fixingCodes.push(this.data.fixing.fixingCode);

        if (this.pendingFixingTime != null && this.pendingFixingTimezone != null) {
          let time = new CubiqTS.Long(Number(this.pendingFixingTime));
          let fixing = new BenchmarkFixingTime(time, this.pendingFixingTimezone);

          this.data.fixing.fixing = fixing;
        }

        if (this.data.fixing.fixingCodes == null) {
          this.data.fixing.fixingCodes = [];
        }

        if (this.pendingFixingDate != null) {
          this.data.fixingDate.value = this.pendingFixingDate;
          this.api.updateFixingDate(this.refId, this.data.fixingDate.value);
          this.pendingFixingDate = null;
          this.loadingFixings = false;
        }
      }
    }
  }

  public async loadPendingControls(force: boolean = false) {
    // skip validation if not loading fixing values
    let validateFixing = this.loadingFixings;

    if (force || this.loadingFixings === false) {
      this.loadingFixings = false;

      if (this.pendingFixingDate) {
        this.selectFixingDate(this.pendingFixingDate);
      }
      if (this.pendingFixingTime) {
        this.selectFixingTime(this.pendingFixingTime);
      }
      if (this.pendingFixingTimezone) {
        this.selectFixingTimezone(this.pendingFixingTimezone);
      }

      if (validateFixing) await this.validateFixing();
      await this.validateFixingTime();
      this.selectFixing();
    }
  }

  public async selectFixingCode(fixingCode: BenchmarkFixingDetail): Promise<boolean> {
    if (fixingCode instanceof BenchmarkFixingDetail) {
      let clientBranch =
        this.data.client != null && this.data.client.value instanceof CubiqTS.ClientImpl
          ? this.data.client.value.branchCd
          : this.clientBranch;

      fixingCode = await this.bmFixService.getBenchmarkDetail(
        fixingCode.code,
        this.data.currencyPair.base,
        this.data.currencyPair.nonBase,
        clientBranch,
      );

      if (
        this.data.fixing.fixing instanceof BenchmarkFixingTime &&
        fixingCode instanceof BenchmarkFixingDetail &&
        Array.isArray(fixingCode.times)
      ) {
        // fix for OMS-31262 (Fixing Time is cleared even when the modified Fixing Code contains the same fixing time)
        let fixingTime = fixingCode.times.find(
          (item: BenchmarkFixingTime) =>
            this.data.fixing.fixing.value === item.value &&
            this.data.fixing.fixing.timezoneCd === item.timezoneCd,
        );

        // If fixing time is unmatched try to locate it there
        if (this.data.unmatchedFixingDetails != null) {
          let item: BenchmarkFixingTime = this.data.unmatchedFixingDetails;
          if (
            this.data.fixing.fixing.value === item.value &&
            this.data.fixing.fixing.timezoneCd === item.timezoneCd
          ) {
            fixingTime = item;
          }
        }

        if (fixingTime == null) {
          this.data.fixing.fixing = null;
          this.data.fixing.fixingState = ICNInputControlState.INACTIVE;
        } else {
          this.data.fixing.fixingState = ICNInputControlState.VALID;
        }
      } else {
        this.data.fixing.fixing = null;
        this.data.fixing.fixingState = ICNInputControlState.INACTIVE;

        this.data.fixing.fixingCode = null;
        this.data.fixing.fixingCodeState = ICNInputControlState.INACTIVE;

        this.data.fixing.fixings = [];
        this.data.fixingDate.disabled = true;
        this.data.fixing.zeroAvailableFixings = false;
      }

      if (fixingCode instanceof BenchmarkFixingDetail) {
        this.data.fixing.fixingCode = fixingCode;
        this.data.fixing.fixings = [...this.data.fixing.fixingCode.times];
        this.data.fixingDate.disabled = false;
        this.data.fixing.zeroAvailableFixings = false;
        this.handleSingleFixingTimeInCode(fixingCode);
      }

      this.selectFixing();
      this.coordinator.cmdUnbulk(this.refId, OENInputControl.FIXING_SOURCE);
      return true;
    } else {
      this.data.fixing.fixingCode = null;
      this.data.fixing.fixing = null;
      this.data.fixing.fixings = [];
      this.data.fixing.zeroAvailableFixings = false;
      this.data.fixingDate = OEPanelBmarkModel.defaultFixingDate;
      this.selectFixingDate(this.data.fixingDate.value);
      // resets to invalid with this
      this.selectFixing();
      this.coordinator.cmdUnbulk(this.refId, OENInputControl.FIXING_SOURCE);
      return false;
    }
  }

  /**
   * Chosen in Ui Control
   */
  public async onFixingChange(newFixing, validate) {
    if (null != newFixing) {
      this.data.fixing.fixing = newFixing;
    }

    this.updateRetrieveMarkupEnabled();

    if (validate) {
      await this.validateFixing();
      this.validateFixingTime();
      this.selectFixing();
    } else {
      this.selectFixing();
    }
  }

  /**
   * Chosen in Ui Control
   */

  public onSelectFixingCode(fixingCode: BenchmarkFixingDetail): void {
    if (fixingCode?.code != null /* && fixingCode.code != this.data.fixing?.fixingCode?.code */) {
      this.onFixingCodeChange(fixingCode);
    }
  }

  public async onBlurFixingCode($event): Promise<void> {
    await this.reloadFixingTimes();

    this.fixingCodeControl.forEach(async (control) => {
      control.cd.markForCheck();
      if (typeof control.value === "string" && control.value != this.data.fixing.fixingCode?.code) {
        logger.out.warn(
          `${this.constructor.name}.onBlurFixingCode() mismatch between model and control. selecting ${control.value}`,
        );
        await this.loadFixingCodeString(control.value);
      }
    });
  }

  public async onFixingCodeChange(newFixingCode: BenchmarkFixingDetail): Promise<void> {
    await this.selectFixingCode(newFixingCode);
    this.updateRetrieveMarkupEnabled();
  }

  /**
   * Chosen in Ui Control
   */
  public onFixingDateChange(newDate): void {
    this.selectFixingDate(newDate);
    this.updateRetrieveMarkupEnabled();

    this.validateFixingTime();
  }

  public onFixingDateBlur(_event: any) {
    this.validateFixingTime();
  }

  public onFixingDateCalendarClose($event: any): void {
    this.validateFixingTime();
  }

  protected selectFixing(forceSelect = false) {
    let bmCode: string = "";
    let fixing: any = null;
    let tz: string = "";
    if (this.data.fixing.fixingCode && this.data.fixing.fixingCode.code) {
      bmCode = this.data.fixing.fixingCode.code;
    }
    if (this.data.fixing.fixing) {
      if (this.data.fixing.fixing.value) {
        fixing = this.data.fixing.fixing.value;
      }

      if (this.data.fixing.fixing.timezoneCd) {
        tz = this.data.fixing.fixing.timezoneCd;
      }
      this.loadValueDateDataset();
    }
    this.coordinator.cmdUnbulk(this.refId, OENInputControl.FIXING_SOURCE);
    this.api.updateFixing(this.refId, bmCode, fixing, tz, forceSelect);
  }

  protected resetFixing(newDate: Date = null) {
    this.data.fixing = OEPanelBmarkModel.defaultFixing;
    this.data.fixingDate = OEPanelBmarkModel.defaultFixingDate;

    // clear floating autocomplete group label state & state input
    if (this.fixingCodeControl != null) {
      this.fixingCodeControl.forEach((c) => {
        c.writeValue(null);
        c.state = BfxAutoCompleteComponentState.INACTIVE;
      });
    }
    if (this.fixingControl != null) {
      this.fixingControl.forEach((c) => {
        c.writeValue(null);
        c.state = BfxAutoCompleteComponentState.INACTIVE;
      });
    }
    this.coordinator.cmdUnbulk(this.refId, OENInputControl.FIXING_SOURCE);
    this.cd.markForCheck();
  }

  protected async resetFixingTime() {
    this.data.fixing.fixings = [];
    this.data.fixing.zeroAvailableFixings = false;
    this.data.fixing.fixing = null;

    await this.loadFixingData();
    this.onFixingChange(this.data.fixing.fixing, true);
  }

  private propagateMarkupDefaultData(): void {
    if (this.data.markup.config.markupEntry) {
      // markupLimitCbx - if enabled sets markup to 0 Pips
      if (!this.data.markup.value) {
        this.data.markup.value = new ICMarkupModel();
      }
      this.data.markup.externalState = null;

      let markup = this.data.markup.value;
      if (markup == null || markup.value == null) {
        // Set default values
        markup.value = BIG_NUMBER_ZERO;
        markup.textValue = BIG_NUMBER_ZERO.toString();
        markup.typeValue = new ICAutocompleteGenericModel(CubiqTS.MarkUpType.Pips, "Pips");
        markup.checkbox.enabled = this.data.markup.config.markupEntry; // true
        markup.checkbox.value = this.data.markup.config.markupCheckboxSelected;
      }

      this.updateMarkup(markup);
      this.displayMarkupRetrievalButton();
      this.api.updateMarkup(this.refId, markup);
      this.cd.markForCheck();
    }
  }

  /**********************************************************************/
  /* Tabs */

  public onTabActivate($event: any) {
    this.tab = $event.index;
  }

  private makeTabsUntabbable(): void {
    let financialsTab = this.getTabAt(0);
    let moreTab = this.getTabAt(1);
    if (financialsTab) {
      this.renderer2.setAttribute(financialsTab, "tabIndex", "-1");
    }
    if (moreTab) {
      this.renderer2.setAttribute(moreTab, "tabIndex", "-1");
    }
  }

  private getTabAt(index) {
    return this.elTabs?.el?.nativeElement?.children[0]?.children[0]?.children[index];
  }

  /**********************************************************************/
  /* COORDINATOR HANDLER */
  private subscribeToCoordinator(): void {
    this.coordinatorSubscription = this.coordinator.entryPanelCmd$.subscribe(
      async ($event: OECommandEventModel) => {
        if ($event.ref === this.refId) {
          this.cd.markForCheck();
          switch ($event.cmd) {
            case OENCommand.RESET_ALL:
              this.reset();
              break;
            case OENCommand.INVALID_INPUT:
              this.handleInvalidInput($event.data);
              break;
            case OENCommand.LOCK_ALL:
              this.lockAll();
              break;
            case OENCommand.UNLOCK_ALL:
              this.unlockAll();
              break;
            case OENCommand.LOAD_LOCKING_PROFILE:
              this.loadLockProfile($event.data);
              break;
            case OENCommand.LOAD_CONTROLS:
              await this.load($event.data);
              if ($event.data instanceof OEDLoadingDataModel && !$event.data.singlePropagation) {
                this.informLoadComplete();
              }
              break;
            case OENCommand.UNBULK:
            case OENCommand.BULKED:
            case OENCommand.BULKING: {
              break;
            }
            default:
              logger.out.error(
                `ERROR: ${
                  this.constructor.name
                }.subscribeToCoordinator(): Command not implemented! command= (${$event.cmd}) [${
                  $event.cmd != null ? OENCommand[$event.cmd] : "NULL"
                }]`,
              );
              break;
          }
        }
      },
    );
  }

  private informLoadComplete(): void {
    this.dataLoadCompleted.emit(true);
  }

  /**********************************************************************/
  /* RESET */
  private reset(): void {
    this.resetData();
    this.resetLocking();
  }

  // handleResetAllControls
  private resetData(): void {
    /********* CLIENT PANEL *********/
    this.icClient.forEach((c) => c.reset());
    this.icAccount.forEach((c) => c.reset());
    this.icClientTrader.forEach((c) => c.reset());
    this.contactsInputControl.forEach((c) => c.resetInputControl());

    /********* FINANCIALS PANEL *********/
    this.data.marketRate = new OERateDisplayModel();
    this.unsubscribeRate(this.rootRefId, this.data.previousValue.product);

    this.data.previousValue.client = null;
    this.data.previousValue.clientId = null;
    this.data.previousValue.inverted = false;
    this.data.previousValue.product = null;

    // Fixed ccy
    /* 		this.data.fixedCcy.ccyPair = null;
		this.data.fixedCcy.currency = null;
		this.data.fixedCcy.valueCcy = null; */
    this.fixedCcyControl.forEach((c) => c.reset());
    this.data.fixedCcy.ccyPair = null;
    //
    this.data.currencyPair.inverted = false;
    this.control.showIntentAndAmount = true;
    //
    this.data.markup.criteria = null;
    // Limit Price
    //		this.data.limitPrice.loadRate = null;
    //
    this.data.currencyPair.product = null;
    this.icCcyPair.forEach((c) => c.reset());
    //
    this.clientIntentToggleControl.forEach((c) => c.resetInputControl());

    this.data.orderAmount.value = null;
    this.orderAmountInputControl.forEach((c) => c.resetInputControl());

    // value date
    this.data.valueDate.dataset = null;
    this.valueDateInputControl.forEach((c) => c.resetInputControl());

    // Markup
    this.markupInputControl.forEach((c) => c.resetInputControl());
    setTimeout(() => {
      this.propagateMarkupDefaultData();
    }, 100);

    this.setOrderType();

    this.resetFixing();

    // traderGroup reset
    this.data.traderGroup.value = null;
    //this.data.traderGroup.externalState = null;
    this.data.traderGroup.traderGroupVisited = false;
    this.data.traderGroup.state = ICNInputControlState.INACTIVE;
    // traderGroup dataset reset
    this.data.traderGroup.traderGroups = [];
    this.data.traderGroup.allTraderGroups = [];
    this.data.traderGroup.limitTraderGroups = [];
    this.data.traderGroup.manualTraderGroups = [];
    this.data.traderGroup.benchmarkTraderGroups = [];
    this.initializeTraderGroups();
    /********* MORE PANEL *********/
    // reset private comment
    this.data.commentPrivate.value = null;
    this.data.commentPrivate.uiPrivatized = false;
    this.data.commentPrivate.externalState = null;
    // reset client comment
    this.data.commentClient.value = null;
    this.data.commentClient.uiPrivatized = false;
    this.data.commentClient.externalState = null;
    this.data.commentClient.checkbox.value = false;
    //  hold stp
    this.icHoldSTP.forEach((c) => c.reset());
    // auto fwd pricing
    this.autoFwdPricing.reset();
    // reset private order
    this.data.privateOrder.show = this.data.privateOrder.showConfig;
    this.data.privateOrder.uiPrivatized = false;
    this.icPrivateOrder.forEach((c) => c.reset());

    // reset custom fields
    this.data.customValues.value = null;
    this.data.customValues.externalState = null;
    this.customFields.forEach((c) => c.resetInputControl());

    // OBOs
    this.data.oboClient.value = null;
    this.data.oboAccount.value = null;

    // multi-asset bulking
    this.isBulked = false;
    this.isBulking = false;
  }

  setOrderType() {
    this.data.orderType.externalState = null;
    this.api.updateOrderType(this.refId, this.data.orderType.value);
  }

  private resetLocking(): void {
    /********* CLIENT PANEL *********/
    this.data.client.lockState = OENLockedState.UNLOCKED;
    this.data.account.lockState = OENLockedState.UNLOCKED;
    this.data.contacts.lockState = OENLockedState.UNLOCKED;
    /********* FINANCIALS PANEL *********/
    this.data.currencyPair.lockState = OENLockedState.UNLOCKED;
    this.data.clientIntent.lockState = OENLockedState.UNLOCKED;
    this.data.fixedCcy.lockState = OENLockedState.UNLOCKED;
    this.data.orderAmount.lockState = OENLockedState.UNLOCKED;
    this.data.markup.lockState = OENLockedState.UNLOCKED;

    this.data.fixing.locked = true;
    this.data.fixing.lockState = OENLockedState.UNLOCKED;

    this.data.traderGroup.lockState = OENLockedState.UNLOCKED;
    /********* MORE PANEL *********/
    this.data.commentPrivate.lockState = OENLockedState.UNLOCKED;
    this.data.commentClient.lockState = OENLockedState.UNLOCKED;
    this.data.customValues.lockState = OENLockedState.UNLOCKED;
    this.data.holdSTP.lockState = OENLockedState.UNLOCKED;
    this.data.privateOrder.lockState = OENLockedState.UNLOCKED;

    this.data.orderType.lockState = OENLockedState.LOCKED;
    /****************************/
    // OBO
    this.data.oboClient.lockState = OENLockedState.UNLOCKED;
    this.data.oboAccount.lockState = OENLockedState.UNLOCKED;

    this.unlockAll();
  }

  private resetMarkup(): void {
    let markup = (this.data.markup.value = new ICMarkupModel());
    markup.value = null;
    markup.textValue = "";
    markup.typeValue = null;
    markup.checkbox.enabled = true;
    markup.checkbox.value = false;
    this.updateMarkup(this.data.markup.value);
    this.data.markup.externalState = null;
    this.api.updateMarkup(this.refId, this.data.markup.value);
    this.markupInputControl.forEach((c) => c.resetInputControl());
  }

  /**********************************************************************/
  /* EXTERNAL INPUT VALIDATION */

  private handleInvalidInput(inputError: OEInputModel): void {
    this.cd.markForCheck();
    if (
      inputError != null &&
      (inputError.panel === OENPanel.PANEL_CLIENT ||
        inputError.panel === OENPanel.PANEL_FINANCIALS ||
        inputError.panel === OENPanel.PANEL_MORE)
    ) {
      switch (inputError.input) {
        /********* CLIENT PANEL *********/
        case OENInputControl.CLIENT:
          // guard added to avoid marking client as invalid on duplicate based on external validation after model synthesis,
          // where the client is null due to visibility, related to OMS-35883
          if (
            this.control.initialLoadingComplete ||
            this.operationMode !== OENOperationMode.DUPLICATE
          ) {
            this.data.client.externalState = ICNInputControlState.INVALID;
          }
          break;
        case OENInputControl.ACCOUNT:
          if (
            this.control.initialLoadingComplete ||
            this.operationMode !== OENOperationMode.DUPLICATE
          ) {
            this.data.account.externalState = ICNInputControlState.INVALID;
          }
          break;
        /********* FINANCIALS PANEL *********/
        case OENInputControl.CCY_PAIR:
          this.data.currencyPair.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.CLIENT_INTENT:
          //this.data.clientIntent.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.FIXED_CCY:
          //this.data.clientIntent.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.ORDER_AMOUNT:
          this.data.orderAmount.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.MARKUP:
          this.data.markup.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.FIXING_SOURCE:
          this.data.fixing.fixingCodeState = ICNInputControlState.INVALID;
          this.fixingCodeControl.forEach((c) => c.markForCheck());
          break;
        case OENInputControl.FIXING_DATE:
          this.data.fixingDate.state = ICNInputControlState.INVALID;
          break;
        case OENInputControl.FIXING_TIME:
          this.data.fixing.fixingState = ICNInputControlState.INVALID;
          this.fixingControl.forEach((c) => {
            // Workaround for case where model id already INVALID and so change won't get pushed (FXBFXOM-11423)
            c.state = ICNInputControlState.INVALID;
            c.markForCheck();
          });
          break;
        case OENInputControl.TRADER_GROUP:
          this.data.traderGroup.state = ICNInputControlState.INVALID;
          break;
        /********* MORE PANEL *********/
        case OENInputControl.CLIENT_COMMENTS:
          this.data.commentClient.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.PRIVATE_COMMENTS:
          this.data.commentPrivate.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.HOLD_STP:
          //this.data.holdSTP.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.PRIVATE_ORDER:
          // this.data.privateOrder.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.CUSTOM_FIELDS:
          this.data.customValues.externalState = ICNInputControlState.INVALID;
          break;
      }
    } else {
      logger.out.error(
        `ERROR: oe${
          OENEntryType[this.entryType]
        }Entry.handleInvalidInput(): NULL or Invalid panel: `,
        inputError,
      );
    }
  }
  /**********************************************************************/
  /* LOAD */

  // handleLoadControls
  private async load(data: OEDLoadingDataModel) {
    if (
      data != null &&
      data instanceof OEDLoadingDataModel &&
      data.values instanceof Array &&
      data.values.length > 0
    ) {
      this.control.isLoading = data.blockPropagation;
      this.control.clientAccountConstraintAutoSelect = false;

      let containsCheck: any = {
        type: false,
        goodFrom: false,
        goodUntil: false,
        //	executionMode: false
      };

      this.loadingControls = true; // to make the dependent controls wait until we have loaed the ficing code
      let fixingDateLoaded: boolean = false;

      for (let item of data.values) {
        /********* CLIENT PANEL *********/
        if (item.panel === OENPanel.PANEL_CLIENT) {
          switch (item.input) {
            case OENInputControl.CLIENT:
              if (
                item.value != null &&
                typeof item.value.label === "string" &&
                (item.value.label === PRIVATIZATION_LABEL ||
                  item.value.label === RESTRICTED_LABEL) &&
                item.value.hasOwnProperty("clientBranch")
              ) {
                this.icClient.forEach((c) => c.setUIPrivatized(item.value.label));
                this.clientBranch = item.value.clientBranch;
              } else {
                this.data.client.value = item.value;
                if (OENOperationMode.MODIFY_INFO === this.operationMode) {
                  this.lockClientForModifyInfo();
                }
                setTimeout(() => {
                  this.icClient.forEach((c) => c.setDataValue(item.value, true, false, true));
                  this.icClient.forEach((c) => c.autocompleteValidate());
                }, 0);
                this.icClientTrader.forEach((c) => (c.input_constraint = item.value));

                this.propagateClientChange();
              }
              break;
            case OENInputControl.ACCOUNT:
              if (
                typeof item.value === "string" &&
                (item.value === PRIVATIZATION_LABEL || item.value === RESTRICTED_LABEL)
              ) {
                this.icAccount.forEach((c) => c.setUIPrivatized(item.value));
              } else {
                this.data.account.value = item.value;
                if (OENOperationMode.MODIFY_INFO === this.operationMode) {
                  this.lockAccountForModifyInfo();
                }
                setTimeout(() => {
                  this.icAccount.forEach((c) => c.setDataValue(item.value, true, true, true));
                  this.icAccount.forEach((c) => c.autocompleteValidate());
                }, 0);
                setTimeout(() => {
                  this.icAccount.forEach((c) => c.checkAndLockAccountForClient());
                }, 300);

                try {
                  if (this.refId != null && item.value instanceof CubiqTS.AccountImpl) {
                    this.api.updateAccount(this.refId, item.value);
                  }
                } catch (e) {
                  logger.out.error(
                    `Failed to update account ${item.value} in bmark model ${this.refId}`,
                  );
                }

                this.propagateAccountChange();
              }
              break;
            case OENInputControl.CONTACT:
              this.data.contacts.value = item.value;
              break;
            case OENInputControl.TRADER:
              if (typeof item.value === "string" && item.value === PRIVATIZATION_LABEL) {
                this.icClientTrader.forEach((c) => c.setUIPrivatized(item.value));
              } else {
                this.data.clientTrader.value = item.value;
                this.icClientTrader.forEach((c) => c.setDataValue(item.value, true, false, true));
                //this.icClientTrader.autocompleteValidate();
              }
              break;
            default:
              break;
          }
          /********* FINANCIALS PANEL *********/
        } else if (item.panel === OENPanel.PANEL_FINANCIALS) {
          switch (item.input) {
            case OENInputControl.CCY_PAIR:
              this.icCcyPair.forEach((c) => c.load(item.value));
              this.onCcyPairBlur(item.value);
              break;
            case OENInputControl.ORDER_TYPE:
              this.icOrderType.forEach((c) => c.setDataValue(item.value));
              break;
            case OENInputControl.CLIENT_INTENT:
              this.clientIntentToggleControl.forEach((c) => c.writeValue(item.value));
              break;
            case OENInputControl.FIXED_CCY:
              if (item.value instanceof ICFixedCcyModel || item.value === null) {
                this.fixedCcyControl.forEach((c) => c.load(item.value, false));
                this.data.fixedCcy.ccyPair = item.value.ccyPair;
                this.data.fixedCcy.fixedCcy = item.value.ccy;
                this.data.fixedCcy.value = item.value;
              }
              break;
            case OENInputControl.ORDER_AMOUNT:
              this.setOrderAmount(item.value);
              break;
            case OENInputControl.MARKUP:
              if (item.value instanceof ICMarkupModel) {
                this.data.markup.value = item.value;
                this.markupInputControl.forEach((c) => c.load(item.value));

                // fix for OMS-33516
                if (item.value.checkbox.enabled && item.value.checkbox.value) {
                  this.displayMarkupRetrievalButton();
                }
              }
              break;
            case OENInputControl.VALUE_DATE:
              if (item.value instanceof ICTenorValueDateModel) {
                this.data.valueDate.value = item.value;
                this.data.valueDate.isSpot =
                  this.data.valueDate.value &&
                  this.data.valueDate.value.tenor == CubiqTS.Tenor.SPOT;
                this.data.valueDate.isBrokenDate =
                  this.data.valueDate.value &&
                  this.data.valueDate.value.tenor == null &&
                  this.data.valueDate.value.valueDate != null;
                this.valueDateInputControl.forEach((c) => c.writeValue(item.value));
              }
              break;
            case OENInputControl.FIXING_SOURCE: {
              // we need to have loaded fixing codes - so the Autocomplete can "find" the value in it
              // otherwise it marks it invalid until such time as you manually select one.
              // so we push in the one we are loading

              await this.loadFixingCodeString(item.value);
              break;
            }
            case OENInputControl.FIXING_DATE: {
              this.selectFixingDate(item.value);
              fixingDateLoaded = true;
              break;
            }
            case OENInputControl.FIXING_TIME: {
              let time: string = item.value;

              if (time != null && time.includes("/")) {
                let times: string[] = time.split("/");
                time = times[0];
              }
              this.selectFixingTime(time);
              break;
            }
            case OENInputControl.FIXING_TIMEZONE: {
              this.selectFixingTimezone(item.value);
              break;
            }
            case OENInputControl.TRADER_GROUP:
              // CubiqTS.TradingGroupImpl
              if (this.operationMode == OENOperationMode.MODIFY) {
                // If no OCR flag or OCR is false then we enable trader group
                this.data.traderGroup.traderGroupModifiable = !(
                  item.value?.orderCancelReplaceFg ?? false
                );
              }
              if (!this.utility.compareObjects(this.data.traderGroup.value, item.value)) {
                // we need to have loaded trader groups - so the Autocomplete can "find" the value in it
                // otherwise it marks it invalid until such time as you manually select one.
                // so we push in the one we are loading
                this.data.traderGroup.value = item.value === "" ? null : item.value;
                //this.data.traderGroup.traderGroups.push(this.data.traderGroup.value);// possible duplicate, already initialized
                this.data.traderGroup.traderGroupVisited = true;
                this.data.traderGroup.traderGroups = this.getTraderGroups();
                this.onTraderGroupChange();
                this.validateTraderGroup();
              }
              break;
            case OENInputControl.ALLOCATIONS: {
              this.data.allocations = item.value.allocations;
              this.data.farAllocations = item.value.farAllocations;
              this.isBulked = item.value.bulked;
              this.isBulking = item.value.bulking;
              this.allocationGridLoadSubject.next(item.value);
              break;
            }
            default:
              break;
          }
          /********* MORE PANEL *********/
        } else if (item.panel === OENPanel.PANEL_MORE) {
          switch (item.input) {
            case OENInputControl.CLIENT_COMMENTS:
              if (typeof item.value === "string" && item.value === PRIVATIZATION_LABEL) {
                this.data.commentClient.value = item.value;
                this.data.commentClient.privatizationLabel = item.value;
                this.data.commentClient.uiPrivatized = true;
                this.data.commentClient.lockState = OENLockedState.LOCKED;
                this.data.commentClient.locked = true;
              } else if (item.value == null || typeof item.value === "string") {
                this.data.commentClient.value = item.value;
              } else {
                logger.out.error(
                  `ERROR: oe${
                    OENEntryType[this.entryType]
                  }Entry.handleLoadControls(): Invalid client comment value!`,
                );
              }
              break;
            case OENInputControl.PRIVATE_COMMENTS:
              if (typeof item.value === "string" && item.value === PRIVATIZATION_LABEL) {
                this.data.commentPrivate.value = item.value;
                this.data.commentPrivate.privatizationLabel = item.value;
                this.data.commentPrivate.uiPrivatized = true;
                this.data.commentPrivate.lockState = OENLockedState.LOCKED;
                this.data.commentPrivate.locked = true;
              } else if (item.value == null || typeof item.value === "string") {
                this.data.commentPrivate.value = item.value;
                this.onCommentPrivateChange();
              } else {
                logger.out.error(
                  `ERROR: oe${
                    OENEntryType[this.entryType]
                  }Entry.handleLoadControls(): Invalid private comment value!`,
                );
              }
              break;
            case OENInputControl.CUSTOM_FIELDS:
              this.customFields.forEach((c) =>
                c.loadCustomValues(item.value.customValues, item.value.hasStrategyFill),
              );
              break;
            case OENInputControl.HOLD_STP:
              this.icHoldSTP.forEach((c) => c.load(item.value));
              break;
            case OENInputControl.AUTO_FORWARD_PRICING:
              this.autoFwdPricing.load(item.value);
              break;
            case OENInputControl.PRIVATE_ORDER:
              if (typeof item.value === "string" && item.value === PRIVATIZATION_LABEL) {
                this.icPrivateOrder.forEach((c) => c.load(true));
                this.data.privateOrder.uiPrivatized = true;
              } else {
                this.icPrivateOrder.forEach((c) => c.load(item.value));
              }
              break;
          }
        } else if (item.panel === OENPanel.PANEL_OBO) {
          switch (item.input) {
            case OENInputControl.OBO_CLIENT:
              this.data.oboClient.value = item.value;
              break;
            case OENInputControl.OBO_ACCOUNT:
              this.data.oboAccount.value = item.value;
              break;
          }
        } else {
          logger.out.error(
            `ERROR: oe${
              OENEntryType[this.entryType]
            }Entry.handleLoadControls(): Invalid panel for item: `,
            item,
          );
        }
      }

      this.loadingControls = false;

      // if none specified (new order) - then "load" the default
      if (!fixingDateLoaded) {
        this.selectFixingDate(this.data.fixingDate.value);
      }
      this.loadPendingControls();

      if (!data.singlePropagation) {
        this.postLoad(containsCheck);
      }

      // set account auto-select on constraint (added because of OMS-35837)
      setTimeout(() => {
        this.control.clientAccountConstraintAutoSelect = true;
      }, 100);
      // setting flag that indicates that data has been loaded at least once
      setTimeout(() => {
        this.control.initialLoadingComplete = true;
      }, 500);
    } else {
      logger.out.error(
        `ERROR: oe${OENEntryType[this.entryType]}Entry.handleLoadControls(): Data is NULL: `,
        data,
      );
    }
  }

  private lockAccountForModifyInfo() {
    this.data.account.lockState = OENLockedState.LOCKED;
    this.icAccount.forEach((c) => c.lock());
  }

  private lockClientForModifyInfo() {
    this.data.client.lockState = OENLockedState.LOCKED;
    this.icClient.forEach((c) => c.lock());
  }

  private postLoad(containsCheck: any): void {
    /********* CLIENT PANEL *********/
    this.contactsInputControl.forEach((contactControl) => {
      contactControl.writeClient(this.data.client.value);
      contactControl.writeClientTrader(this.data.clientTrader.value);
      contactControl.writeValue(this.data.contacts.value);
      contactControl.loadComplete();
    });

    /********* FINANCIALS PANEL *********/
    /* if (!containsCheck.type)
			this.orderTypeGetDataset(); */ // needed for TSL on Loops

    this.cd.markForCheck();

    setTimeout(() => {
      this.control.isLoading = false;
    });
  }

  /**********************************************************************/
  /* LOCKING */

  private lockAll(): void {
    this.control.lockAllFg = true;
    this.updateLocking();
  }

  private unlockAll(): void {
    this.control.lockAllFg = false;
    this.updateLocking();
  }

  protected loadLockProfile(lockingProfile: OELockingProfileTransportModel): void {
    if (lockingProfile instanceof OELockingProfileTransportModel) {
      for (let lockStateItem of lockingProfile.profile) {
        if (
          lockStateItem.panel === OENPanel.PANEL_CLIENT ||
          lockStateItem.panel === OENPanel.PANEL_FINANCIALS ||
          lockStateItem.panel === OENPanel.PANEL_MORE ||
          lockStateItem.panel === OENPanel.PANEL_OBO
        ) {
          this.assignLockStateToData(lockStateItem);
        }
      }
      if (lockingProfile.profileType === OENLockingProfileType.PERSISTENT) {
        this.control.lockingProfileData = lockingProfile.profile;
      }
      this.updateLocking();
    } else {
      logger.out.warn(`WARNING: oeBmarkPanel.loadLockProfile(): Invalid locking profile`);
    }
  }

  private assignLockStateToData(lockStateItem: OEInputLockStateItemModel): void {
    switch (lockStateItem.input) {
      /********* CLIENT PANEL *********/
      case OENInputControl.CLIENT:
        this.data.client.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.ACCOUNT:
        this.data.account.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.CONTACT:
        this.data.contacts.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.TRADER:
        this.data.clientTrader.lockState = lockStateItem.lockState;
        break;

      /********* FINANCIALS PANEL *********/
      case OENInputControl.ACCOUNT_ACTIVE_TAB:
      case OENInputControl.CLIENT_ACTIVE_TAB:
        // Do nothing, not in use for locking
        break;
      case OENInputControl.CCY_PAIR:
        this.data.currencyPair.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.CLIENT_INTENT:
        this.data.clientIntent.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.FIXED_CCY:
        this.data.fixedCcy.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.ORDER_AMOUNT:
        this.data.orderAmount.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.MARKUP:
        this.data.markup.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.FIXING_DATE:
        this.data.fixingDate.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.FIXING_SOURCE:
        this.data.fixing.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.TRADER_GROUP:
        this.data.traderGroup.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.VALUE_DATE:
        this.data.valueDate.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.ORDER_TYPE:
        break;
      /********* MORE PANEL *********/
      case OENInputControl.CLIENT_COMMENTS:
        this.data.commentClient.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.PRIVATE_COMMENTS:
        this.data.commentPrivate.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.CUSTOM_FIELDS:
        this.data.customValues.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.PRIVATE_ORDER:
        this.data.privateOrder.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.HOLD_STP:
        this.data.holdSTP.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.OBO_CLIENT:
        this.data.oboClient.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.OBO_ACCOUNT:
        this.data.oboAccount.lockState = lockStateItem.lockState;
        break;
      default:
        logger.out.info(
          `ERROR: oe${
            OENEntryType[this.entryType]
          }Entry.handleLoadLockProfile(): Unhandled lock item: %O`,
          lockStateItem,
        );
        break;
    }
  }

  private updateLocking(): void {
    if (!this.control.lockAllFg) {
      // lockAll is INACTIVE

      //client
      if (this.data.client.lockState === OENLockedState.LOCKED) {
        this.icClient.forEach((c) => c.lock());
      } else if (this.data.client.lockState === OENLockedState.UNLOCKED) {
        this.icClient.forEach((c) => c.unlock());
      }
      // account
      if (this.data.account.lockState === OENLockedState.LOCKED) {
        this.icAccount.forEach((c) => c.lock());
      } else if (this.data.account.lockState === OENLockedState.UNLOCKED) {
        this.icAccount.forEach((c) => c.unlock());
      }
      // contact
      if (this.data.contacts.lockState === OENLockedState.LOCKED) {
        this.contactsInputControl.forEach((c) => c.lockInputControl());
      } else if (this.data.contacts.lockState === OENLockedState.UNLOCKED) {
        this.contactsInputControl.forEach((c) => c.unlockInputControl());
      }
      // client trader
      if (this.data.clientTrader.lockState === OENLockedState.LOCKED) {
        this.icClientTrader.forEach((c) => c.lock());
      } else if (this.data.clientTrader.lockState === OENLockedState.UNLOCKED) {
        this.icClientTrader.forEach((c) => c.unlock());
      }

      // CCY_PAIR:
      if (this.data.currencyPair.lockState === OENLockedState.LOCKED) {
        this.icCcyPair.forEach((c) => c.lock());
      } else if (this.data.currencyPair.lockState === OENLockedState.UNLOCKED) {
        this.icCcyPair.forEach((c) => c.unlock());
      }

      // CLIENT_INTENT:
      if (this.data.clientIntent.lockState === OENLockedState.LOCKED) {
        this.clientIntentToggleControl.forEach((c) => c.lockInputControl());
      } else if (this.data.clientIntent.lockState === OENLockedState.UNLOCKED) {
        this.clientIntentToggleControl.forEach((c) => c.unlockInputControl());
      }
      // FIXED_CCY:
      if (this.data.fixedCcy.lockState === OENLockedState.LOCKED) {
        this.fixedCcyControl.forEach((c) => c.lock());
      } else if (this.data.fixedCcy.lockState === OENLockedState.UNLOCKED) {
        this.fixedCcyControl.forEach((c) => c.unlock());
      }
      // ORDER_AMOUNT:
      if (this.data.orderAmount.lockState === OENLockedState.LOCKED) {
        this.orderAmountInputControl.forEach((c) => c.lockInputControl());
      } else if (this.data.orderAmount.lockState === OENLockedState.UNLOCKED) {
        this.orderAmountInputControl.forEach((c) => c.unlockInputControl());
      }
      // FIXING_DATE:
      if (this.calendar != null) {
        this.calendar.disabled = this.data.fixingDate.lockState === OENLockedState.LOCKED;
      }
      // VALUE_DATE:
      if (this.data.valueDate.lockState === OENLockedState.LOCKED) {
        this.valueDateInputControl.forEach((c) => c.lockInputControl());
      } else if (this.fwdEnabled && this.data.valueDate.lockState === OENLockedState.UNLOCKED) {
        this.valueDateInputControl.forEach((c) => c.unlockInputControl());
      }
      // MARKUP:
      if (this.data.markup.lockState === OENLockedState.LOCKED) {
        this.markupInputControl.forEach((c) => c.lockInputControl());
      } else if (this.data.markup.lockState === OENLockedState.UNLOCKED) {
        this.markupInputControl.forEach((c) => c.unlockInputControl());
      }

      if (this.data.fixing.lockState === OENLockedState.LOCKED) {
        this.data.fixing.locked = true;
      } else if (this.data.fixing.lockState === OENLockedState.UNLOCKED) {
        this.data.fixing.locked = false;
      }

      // TRADER GROUP
      if (this.data.traderGroup.lockState === OENLockedState.LOCKED) {
        this.data.traderGroup.locked = true;
      } else if (this.data.traderGroup.lockState === OENLockedState.UNLOCKED) {
        this.data.traderGroup.locked = false;
      }

      // PRIVATE COMMENTS
      if (this.data.commentPrivate.uiPrivatized) {
        // privatization locked (lock state is lower priority)
        this.data.commentPrivate.locked = true;
      } else if (this.data.commentPrivate.lockState === OENLockedState.LOCKED) {
        this.data.commentPrivate.locked = true;
      } else if (this.data.commentPrivate.lockState === OENLockedState.UNLOCKED) {
        if (this.rootRefId != this.refId && !this.settings.independentComments) {
          this.data.commentPrivate.locked = true;
        } else {
          this.data.commentPrivate.locked = false;
        }
      } else {
        this.data.commentPrivate.locked = false;
        logger.out.error(`Invalid MoreDetails->Private Comment lock state!`); // remove after dev testing
      }
      // CLIENT COMMENTS:
      if (this.data.commentClient.uiPrivatized) {
        // privatization locked (lock state is lower priority)
        this.data.commentClient.locked = true;
      } else if (this.data.commentClient.lockState === OENLockedState.LOCKED) {
        this.data.commentClient.locked = true;
      } else if (this.data.commentClient.lockState === OENLockedState.UNLOCKED) {
        if (this.rootRefId != this.refId && !this.settings.independentComments) {
          this.data.commentClient.locked = true;
        } else {
          this.data.commentClient.locked = false;
        }
      } else {
        this.data.commentClient.locked = false;
        logger.out.error(`Invalid MoreDetails->Client Comment lock state!`);
      }
      // Custom Fields
      if (this.data.customValues.lockState === OENLockedState.LOCKED) {
        this.customFields.forEach((c) => c.lockInputControl());
      } else if (this.data.customValues.lockState === OENLockedState.UNLOCKED) {
        this.customFields.forEach((c) => c.unlockInputControl());
      }
      // HOLD STP
      if (this.data.holdSTP.lockState === OENLockedState.LOCKED) {
        this.icHoldSTP.forEach((c) => c.lock());
      } else if (this.data.holdSTP.lockState === OENLockedState.UNLOCKED) {
        this.icHoldSTP.forEach((c) => c.unlock());
      }
      // AUTO FORWARD PRICING
      if (this.data.autoFwdPricing.lockState === OENLockedState.LOCKED) {
        this.autoFwdPricing.lock();
      } else if (this.data.autoFwdPricing.lockState === OENLockedState.UNLOCKED) {
        this.autoFwdPricing.unlock();
      }

      // PRIVATE ORDER
      if (this.data.privateOrder.uiPrivatized) {
        // privatization locked
        this.icPrivateOrder.forEach((c) => c.lock());
      } else if (this.data.privateOrder.lockState === OENLockedState.LOCKED) {
        this.icPrivateOrder.forEach((c) => c.lock());
      } else if (this.data.privateOrder.lockState === OENLockedState.UNLOCKED) {
        this.icPrivateOrder.forEach((c) => c.unlock());
      }

      // OBO
      this.data.oboClient.disabled = this.data.oboClient.lockState === OENLockedState.LOCKED;
      this.data.oboAccount.disabled = this.data.oboAccount.lockState === OENLockedState.LOCKED;
    } else {
      // lockAll is ACTIVE
      // CLIENT
      this.icClient.forEach((c) => c.lock());
      this.icAccount.forEach((c) => c.lock());
      this.icClientTrader.forEach((c) => c.lock());
      this.contactsInputControl.forEach((c) => c.lockInputControl());
      // FINANCIALS
      this.icCcyPair.forEach((c) => c.lock());
      this.clientIntentToggleControl.forEach((c) => c.lockInputControl());
      this.fixedCcyControl.forEach((c) => c.lock());
      this.orderAmountInputControl.forEach((c) => c.lockInputControl());
      this.valueDateInputControl.forEach((c) => c.lockInputControl());
      this.markupInputControl.forEach((c) => c.lockInputControl());

      this.data.fixing.locked = true;

      this.data.traderGroup.locked = true;

      // MORE
      this.data.commentPrivate.locked = true;
      this.data.commentClient.locked = true;
      this.data.customValues.locked = true;
      this.icHoldSTP.forEach((c) => c.lock());
      this.autoFwdPricing.lock();
      this.icPrivateOrder.forEach((c) => c.lock());
      this.customFields.forEach((c) => c.lockInputControl());

      // OBO
      this.data.oboClient.disabled = true;
      this.data.oboAccount.disabled = true;
    }
  }

  /**********************************************************************/
  /* DATASET  */

  private displayMarkupRetrievalButton(): void {
    this.updateRetrieveMarkupEnabled();

    if (
      this.data.markup.config.markupRetrievalEnabled &&
      this.data.markup.config.markupType !== "MANUAL"
    ) {
      this.markupInputControl.forEach((c) => c.displayMarkupRefresh(true));
    }
  }

  /**
   * Load all currency pairs from the replicate provided base lookup service
   * and fill in the appropriate currency pair values (inverted too, where applicable)
   * in the data model
   */
  private currencyPairsGetDataset(): void {
    let dataset = this.api.getBmarkCcyPairsDataset();
    if (dataset != null && dataset instanceof Array && dataset.length > 0) {
      this.data.currencyPair.dataset = dataset.slice();
    } else {
      logger.out.error(
        `ERROR: oe${
          OENEntryType[this.entryType]
        }Entry.currencyPairsGetDataset(): CcyPairs dataset load failed!`,
      );
      this.data.currencyPair.dataset = [];
    }
  }

  /**********************************************************************/
  /* CONTACTS MANAGER - CLIENT  */

  public onContactManagerOpen($event: any): void {
    this.api.cmdFreezeInterfaceControls(this.refId);
  }

  public onContactManagerClosed($event: any): void {
    this.api.cmdUnfreezeInterfaceControls(this.refId);
  }

  /**********************************************************************/
  /* CONTROL SETTERS - CLIENT  */

  public onClientChange($event: any): void {
    this.cd.markForCheck();
    this.data.client.externalState = null;
    this.api.updateClient(this.refId, this.data.client.value);
    this.updateRetrieveMarkupEnabled();
    this.loadFixingData().then(() => {
      this.validateFixingTime();
    });
  }

  public onClientSelect($event: any): void {
    let clientId = $event;
    this.oeLogger.outputDevLogToConsole("PANEL Client PREDICTED VALUE: ", false, $event);
    this.evaluateMarkupByBranch(clientId);
  }

  public onAccountChange($event: any): void {
    this.cd.markForCheck();
    this.data.account.externalState = null;
    this.api.updateAccount(this.refId, this.data.account.value);
    this.updateRetrieveMarkupEnabled();
  }

  public onBulkAccountChange(accountCode: string): void {
    this.cd.markForCheck();
    if (this.refId == null) return;

    let value = this.data.account.dataset.find((a) => a.id === accountCode);
    if (value !== undefined) {
      this.data.account.value = value;
      this.data.account.externalState = null;
      this.api.updateAccount(this.refId, this.data.account.value);
      this.updateRetrieveMarkupEnabled();
    }
  }

  public onAccountDatasetChange($event): void {
    this.data.account.dataset = $event;
  }

  public onAllocationsChange(allocations): void {
    this.data.allocations.value = allocations;
    if (this.refId != null) {
      this.api.updateAllocations(this.refId, this.data.allocations.value);
    }
  }

  public onFarAllocationsChange(farAllocations): void {
    this.data.farAllocations.value = farAllocations;
    if (this.refId != null) {
      this.api.updateFarAllocations(this.refId, this.data.farAllocations.value);
    }
  }

  public onProductChange(product): void {
    this.data.product.value = product;
    if (this.refId != null) {
      this.api.updateProduct(this.refId, this.data.product.value);
    }
  }

  public onSwapFixedAmountChange(swapFixedAmount): void {
    this.data.swapFixedAmount.value = swapFixedAmount;
    if (this.refId != null) {
      this.api.updateSwapFixedAmount(this.refId, this.data.swapFixedAmount.value);
    }
  }

  public onSwapTenorChange(swapTenor): void {
    this.data.swapTenor.value = swapTenor;
    if (this.refId != null) {
      this.api.updateSwapTenor(this.refId, this.data.swapTenor.value);
    }
  }

  public onSwapValueDateChange(swapValueDate): void {
    this.data.swapValueDate.value = swapValueDate;
    if (this.refId != null) {
      this.api.updateSwapValueDate(this.refId, this.data.swapValueDate.value);
    }
  }
  public onContactsChange($event: any): void {
    this.data.contacts.externalState = null;
    this.data.contacts.value = $event;
    this.api.updateContacts(this.refId, this.data.contacts.value);
    // propagate
    this.propagateClientChange();
  }

  public onClientTraderChange($event: any): void {
    this.data.clientTrader.externalState = null;
    this.api.updateClientTrader(this.refId, this.data.clientTrader.value);
  }

  public onClientTraderUpdate($event: any): void {
    this.data.clientTrader.value = $event;

    this.cd.markForCheck();

    if (this.icClientTrader != null) {
      // OMS-23843
      this.icClientTrader.forEach((c) =>
        c.setDataValue(this.data.clientTrader.value, true, false, true),
      );
      this.icClientTrader.forEach((c) => c.autocompleteValidate());
    }

    this.data.clientTrader.externalState = null;
    this.api.updateClientTrader(this.refId, this.data.clientTrader.value);
  }

  public onClientDefaultCommentsChanged($event: any): void {
    // updating client default comments overrides previously set independent comments
    this.data.commentClient.externalState = null;
    if ($event.hasClientTraderComment) {
      this.api.updateClientTraderDefaultComment(this.refId, $event.clientTraderComment);
      this.api.updateClientDefaultComments(this.refId, $event.clientComment, $event.privateComment);
    } else {
      this.api.updateClientDefaultComments(
        this.refId,
        this.data.client.value.defaultClientComment,
        this.data.client.value.defaultPrivateComment,
      );
      this.propagateClientDefaultCommentsChange($event);
    }
  }

  /**********************************************************************/
  /* CONTROL SETTERS - FINANCIALS  */

  public onCcyPairBlur($event: any): void {
    let ccyPairIsNew: boolean = this.setCurrencyPair($event);
    if (ccyPairIsNew) {
      this.data.currencyPair.externalState = null;
      this.api.updateCcyPair(
        this.refId,
        this.data.currencyPair.product,
        this.data.currencyPair.model,
      );
      // NB : this.data.fixedCcy updated in setCurrencyPair - but never "setter'd"
      this.api.updateFixedCcy(this.refId, this.data.fixedCcy.value);

      ///
      this.updateRetrieveMarkupEnabled();

      /// fix for OMS-31261 (Fixing Code and Time are cleared when modifying the ccy to a pair that is also enabled for that fixing code)
      if (
        typeof this.data.currencyPair.product !== "string" ||
        this.data.currencyPair.product.length !== 6
      ) {
        return;
      }

      this.reloadFixingTimes();
    }
  }

  async reloadFixingTimes() {
    await this.loadFixingData();
    if (this.data.fixing.fixingCode != null) {
      let bmarkFixingDetails: Array<BenchmarkFixingDetail> = await this.validateFixing();
      // promise needed for sync
      let product = this.data.currencyPair.product;
      let normalizedPair = this.data.currencyPair.inverted
        ? product.substring(3, 6) + product.substring(0, 3)
        : product;
      let fixingDetail = bmarkFixingDetails.find(
        (fixingCode: BenchmarkFixingDetail) =>
          this.compareCcyPairs(normalizedPair, fixingCode.pair) &&
          fixingCode.code === this.data.fixing.fixingCode.code,
      );

      if (fixingDetail == null || fixingDetail instanceof BenchmarkFixingDetail === false) {
        this.data.fixing.fixingCodeState = ICNInputControlState.INVALID;
        return;
      }

      await this.selectFixingCode(fixingDetail);
      if (this.data.fixing.fixing != null) {
        let fixingTime = fixingDetail.times.find((item: BenchmarkFixingTime) => {
          return (
            this.data.fixing.fixing.value === item.value &&
            this.data.fixing.fixing.timezoneCd === item.timezoneCd
          );
        });

        if (fixingTime == null && this.data.unmatchedFixingDetails != null) {
          fixingTime = this.data.unmatchedFixingDetails;
        }

        if (fixingTime == null) {
          await this.resetFixingTime();
        } else {
          await this.validateFixingTime();
        }
      }
    }
  }

  /// fix for OMS-31388
  public compareCcyPairs(pairA: string, pairB: string): boolean {
    if (
      typeof pairA === "string" &&
      pairA.length === 6 &&
      typeof pairB === "string" &&
      pairB.length === 6
    ) {
      let pairAbase = pairA.substr(0, 3).toLowerCase();
      let pairAnonBase = pairA.substr(3, 3).toLowerCase();
      let pairBbase = pairB.substr(0, 3).toLowerCase();
      let pairBnonBase = pairB.substr(3, 3).toLowerCase();
      return (
        ((pairAbase === pairBbase || pairAbase === "***" || pairBbase === "***") &&
          (pairAnonBase === pairBnonBase || pairAnonBase === "***" || pairBnonBase === "***")) ||
        (pairAnonBase === pairBbase && pairBnonBase === "***")
      );
    }
    return false;
  }

  public onClientIntentToggleChange($event: any): void {
    this.data.clientIntent.externalState = null;
    this.api.updateClientIntent(this.refId, this.data.clientIntent.value);
    this.updateRetrieveMarkupEnabled();
  }

  public onFixedCcyToggleChange($event: any): void {
    let updatedFixedCcy = false;
    if (
      this.data.fixedCcy.value != null &&
      typeof this.data.fixedCcy.value.ccy === "string" &&
      this.data.fixedCcy.value.ccy.length === 3
    ) {
      updatedFixedCcy = this.data.fixedCcy.fixedCcy !== this.data.fixedCcy.value.ccy;
      this.data.fixedCcy.fixedCcy = this.data.fixedCcy.value.ccy;
    } else if (this.data.fixedCcy.fixedCcy !== null) {
      this.data.fixedCcy.fixedCcy = null;
      updatedFixedCcy = true;
    }

    if (updatedFixedCcy) {
      this.data.fixedCcy = { ...this.data.fixedCcy };
      this.api.updateFixedCcy(this.refId, this.data.fixedCcy.value);
      this.updateRetrieveMarkupEnabled();
    }
    this.cd.markForCheck();
  }

  public setOrderAmount(newAmount: any): void {
    if (this.data.orderAmount.value !== newAmount) {
      this.data.orderAmount.value = newAmount;
      this.data.orderAmount.externalState = null;

      this.orderAmountInputControl.forEach((c) => c.loadValue(this.data.orderAmount.value));
      this.api.updateOrderAmount(this.refId, this.data.orderAmount.value);
    }
    this.cd.markForCheck();
  }

  public onOrderAmountChange($event: any): void {
    this.data.orderAmount.externalState = null;
    this.api.updateOrderAmount(this.refId, $event);
  }

  public onOrderAmountBlur($event: any): void {
    let orderAmountUserValue = new CubiqTS.BigNumber(this.control.multiplyAmountUserValue);
    if (
      this.data.orderAmount.value != null &&
      this.data.orderAmount.value.isLessThan(orderAmountUserValue) &&
      this.control.multiplyAmountUserToggle === true
    ) {
      this.data.orderAmount.externalState = null;
      this.data.orderAmount.value = this.data.orderAmount.value.times(1000000);
    }

    this.api.updateOrderAmount(this.refId, this.data.orderAmount.value);
    //
    this.updateRetrieveMarkupEnabled();
    this.cd.markForCheck();
  }

  public onBulkOrderIntentChange($event: any): void {
    if (this.refId == null) return;
    this.data.clientIntent.externalState = null;
    this.data.clientIntent.value = $event;
    this.api.updateClientIntent(this.refId, $event);
    this.cd.markForCheck();
  }

  public onBulkFixedCcyChange($event: any): void {
    if (this.refId == null) return;

    let value = new ICFixedCcyModel($event, this.data.fixedCcy.ccyPair);

    if (this.fixedCcyControl != null) {
      this.fixedCcyControl.forEach((i) => i.load(value, true));
    }
    this.data.fixedCcy.fixedCcy = $event;
    this.data.fixedCcy.value = value;
    this.api.updateFixedCcy(this.refId, value);
    this.cd.markForCheck();
  }

  public onBulkOrderAmountChange($event: any): void {
    if (this.refId == null) return;

    this.data.orderAmount.value = $event;
    this.data.orderAmount.externalState = null;
    this.api.updateOrderAmount(this.refId, this.data.orderAmount.value);
    this.cd.markForCheck();
  }

  public onBulkValueDateChange($event: {
    tenor: CubiqTS.Tenor;
    valueDate: CubiqTS.StandardDate;
  }): void {
    if (this.refId == null) return;

    let date = null;

    if ($event == null) {
      return;
    }

    if ($event.valueDate != null && $event.valueDate instanceof CubiqTS.StandardDateImpl) {
      date = new Date(0);
      date.setFullYear($event.valueDate.year);
      date.setMonth($event.valueDate.month - 1);
      date.setDate($event.valueDate.day);
    } else if ($event.valueDate != null && $event.valueDate instanceof Date) {
      date = $event.valueDate;
    }
    this.data.valueDate.value = new ICTenorValueDateModel($event.tenor, date);
    this.onValueDateChange(null);
    this.cd.markForCheck();
  }

  public onValueDateChange($event: any): void {
    this.data.valueDate.isSpot =
      this.data.valueDate.value && this.data.valueDate.value.tenor == CubiqTS.Tenor.SPOT
        ? true
        : false;
    this.data.valueDate.isBrokenDate =
      this.data.valueDate.value &&
      this.data.valueDate.value.tenor == null &&
      this.data.valueDate.value.valueDate != null
        ? true
        : false;
    this.data.valueDate.externalState = null;
    this.api.updateValueDate(this.refId, this.data.valueDate.value);
    this.cd.markForCheck();
  }

  public onMarkupChange($event: any): void {
    if (
      this.data.markup.value instanceof ICMarkupModel &&
      this.data.markup.value !== null &&
      this.data.markup.config.markupEnabled &&
      this.data.markup.config.markupEntry
    ) {
      this.data.markup.externalState = null;
      this.api.updateMarkup(this.refId, this.data.markup.value);
      this.cd.markForCheck();
    }
  }

  public onMarkupCheckboxState(state: boolean): void {
    this.markupCheckboxEnabled = state;
    if (state) {
      if (this.data.markup.config.markupEntry) {
        // markupLimitCbx - if enabled sets markup to 0 Pips
        if (!this.data.markup.value) {
          this.data.markup.value = new ICMarkupModel();
          this.data.markup.value.checkbox.value = state;
          this.data.markup.value.checkbox.enabled = this.data.markup.config.markupEntry;
        }
        let markup = this.data.markup.value;
        let checkSelected = markup.checkbox.enabled == true && markup.checkbox.value == true;
        if (this.data.markup.config.markupCheckboxSelected || checkSelected) {
          // Set default values
          markup.value = BIG_NUMBER_ZERO;
          markup.textValue = BIG_NUMBER_ZERO.toString();
          markup.typeValue = new ICAutocompleteGenericModel(CubiqTS.MarkUpType.Pips, "Pips");
          markup.checkbox.enabled = true;
          markup.checkbox.value = true;
        }

        this.updateMarkup(markup);
        this.displayMarkupRetrievalButton();
        this.api.updateMarkup(this.refId, markup);
      }

      this.propagateMarkupDefaultData();
    } else {
      this.resetMarkup();
    }
    this.cd.markForCheck();
  }

  public onTraderGroupChange(): void {
    this.outputExtendedLogging(
      `${this.constructor.name}.onTraderGroupChange(): MODEL UPDATE -> .value: `,
      this.data.traderGroup.value,
    );
    this.api.updateTraderGroup(this.refId, this.data.traderGroup.value);
  }

  /**********************************************************************/
  /* CONTROL -> MODEL SETTERS - MORE PANEL */

  public onCommentPrivateChange(): void {
    if (!this.data.commentPrivate.uiPrivatized) {
      this.data.commentPrivate.externalState = null;
      this.api.updatePrivateComment(this.refId, this.data.commentPrivate.value);
      this.cd.markForCheck();
    }
  }

  public onCommentClientChange(): void {
    if (!this.data.commentClient.uiPrivatized) {
      this.data.commentClient.externalState = null;
      this.api.updateClientComment(this.refId, this.data.commentClient.value);
      this.cd.markForCheck();
    }
  }

  public onCustomValuesChange(values: Array<CubiqTS.CustomValue>): void {
    this.data.customValues.value = values;
    this.data.customValues.externalState = null;
    this.api.updateCustomFields(this.refId, this.data.customValues.value);
    this.cd.markForCheck();
  }

  public onCommentClientCheckboxChange(): void {
    setTimeout(() => {
      if (
        typeof this.data.commentClient.checkbox.value === "boolean" &&
        this.data.commentClient.checkbox.value
      ) {
        this.cd.markForCheck();

        if (
          this.data.commentClient.externalState === ICNInputControlState.INVALID &&
          this.data.commentClient.checkbox.value
        ) {
          this.data.commentClient.externalState = null;
        }

        this.icCommentClient.nativeElement.focus();
      }
    }, 0);
  }

  public onHoldSTPChange(): void {
    this.api.updateHoldSTP(this.refId, this.data.holdSTP.value);
  }

  public onAutoFwdPricingChange(): void {
    logger.out.info("Got Auto fwd price change: " + this.data.autoFwdPricing.value);
    this.api.updateAutoForwardPrice(this.refId, this.data.autoFwdPricing.value);
  }

  public onPrivateOrderChange(): void {
    if (!this.data.privateOrder.uiPrivatized) {
      this.api.updatePrivateOrder(this.refId, this.data.privateOrder.value);
    }
  }

  /**********************************************************************/
  /* TRADER GROUP METHODS */

  /**
   * Loads and populates lists of trader groups
   */
  protected loadTraderGroups(): void {
    let lookup: TraderGroupLookupService = this.traderGroupService.getBase();
    let all: Array<CubiqTS.TradingGroupImpl> = lookup.getAll(); //.filter((traderGroup: CubiqTS.TradingGroupImpl) => traderGroup.groupId.toNumber() >= 0);
    if (all != null && all instanceof Array && all.length > 0) {
      all = all.filter(
        (traderGroup: CubiqTS.TradingGroupImpl) => traderGroup.groupId.toNumber() >= 0,
      );

      all.forEach((value: CubiqTS.TradingGroupImpl) => (value["id"] = value.groupId.toString()));
      all.sort((a: CubiqTS.TradingGroupImpl, b: CubiqTS.TradingGroupImpl) =>
        a.groupCode.toLowerCase().localeCompare(b.groupCode.toLowerCase()),
      );

      this.data.traderGroup.allTraderGroups = all;
      this.data.traderGroup.benchmarkTraderGroups =
        /* ?? BMVoice Groups ??*/
        all.filter(
          (tradingGroup: CubiqTS.TradingGroupImpl) =>
            tradingGroup.isBenchmarkFg &&
            !tradingGroup.isExternallyFilledFg &&
            !tradingGroup.isAutoFilledFg,
        );
    } else {
      this.data.traderGroup.allTraderGroups = [];
      this.data.traderGroup.benchmarkTraderGroups = [];
    }
    this.data.traderGroup.limitTraderGroups = [];
    this.data.traderGroup.manualTraderGroups = [];
    this.data.traderGroup.algoTraderGroups = [];
    this.cd.markForCheck();
  }

  /**
   * Searches the trader group suggestions for a infix matching of the given filter string
   * @param filter the filter should be checked to be an infix of the target trader group
   */
  public searchTraderGroup(filter: any): void {
    let traderGroups: Array<CubiqTS.TradingGroupImpl> = this.getTraderGroups();

    let query: string = filter.query.toLowerCase();
    if (query.length > 0) {
      this.data.traderGroup.traderGroups = traderGroups.filter((tradeGroup: any) =>
        tradeGroup.groupCode.toLowerCase().includes(query),
      );
    } else {
      this.data.traderGroup.traderGroups = traderGroups.slice(0);
    }
    this.cd.markForCheck();
  }

  public onSelectTraderGroup(data: any): void {
    this.data.traderGroup.traderGroupVisited = true;
    // empty string correction
    this.data.traderGroup.value =
      this.data.traderGroup.value === "" ? null : this.data.traderGroup.value;
    this.outputExtendedLogging(
      `${this.constructor.name}onSelectTraderGroup(): UPDATE -> value: `,
      this.data.traderGroup.value,
    );
    this.validateTraderGroup();
    this.onTraderGroupChange();
    this.cd.markForCheck();
  }

  public traderGroupFocus(): void {
    this.data.traderGroup.traderGroupVisited = true;
  }

  private async validateFixing(): Promise<Array<BenchmarkFixingDetail>> {
    if (this.data.fixing == null) return [];

    const bmarkFixingDetails: Array<BenchmarkFixingDetail> = await this.getFixingData();
    // promise needed for sync
    let bmCodeFound: BenchmarkFixingDetail = null;
    if (this.data.fixing.fixingCode) {
      bmCodeFound = bmarkFixingDetails.find(
        (fixingCode: BenchmarkFixingDetail) => fixingCode.code === this.data.fixing.fixingCode.code,
      );
    }

    // see if the benchmark code "exists" anymore
    if (!bmCodeFound) {
      logger.out.debug(
        "validateFixing: specified benchmark code [" + this.data.fixing.fixingCode + "] not found",
      );
      this.data.fixing.fixingCodeState =
        ICNInputControlState.VALID == this.data.fixing.fixingCodeState
          ? ICNInputControlState.INVALID
          : ICNInputControlState.INACTIVE;
    } else {
      // edit after reject
      this.data.fixing.fixingCodeState = ICNInputControlState.VALID;
    }

    // see if the fixing "exists" anymore
    let fixFound: BenchmarkFixingTime = null;
    if (bmCodeFound != null && this.data.fixing.fixing && this.data.fixing.fixing.value) {
      fixFound = bmCodeFound.times.find(
        (fixingCode: BenchmarkFixingTime) => fixingCode.value == this.data.fixing.fixing.value,
      );
      if (!fixFound) {
        logger.out.debug(
          "validateFixing: specified fixing [" + this.data.fixing.fixing.value + "] not found",
        );
      }
    }

    if (!fixFound) {
      this.data.fixing.fixingState =
        ICNInputControlState.VALID == this.data.fixing.fixingState
          ? ICNInputControlState.INVALID
          : ICNInputControlState.INACTIVE;
    } else {
      // edit after reject
      this.data.fixing.fixingState = ICNInputControlState.VALID;
    }

    this.fixingCodeControl.forEach((c) => c.markForCheck());
    this.fixingControl.forEach((c) => c.markForCheck());

    this.cd.markForCheck();
    return bmarkFixingDetails;
  }

  validateFixingTime(): Promise<void> {
    let dateState = this.data.fixingDate.state;
    let timeState = this.data.fixing.fixingState;

    if (this.data.fixingDate && this.data.fixingDate.value) {
      let isFixingDatePast = this._oeTimeMachine.isFixingDatePast(this.data.fixingDate.value);
      dateState = isFixingDatePast ? ICNInputControlState.INVALID : ICNInputControlState.VALID;

      if (this.data.fixing.fixing && this.data.fixing.fixing.value) {
        let isFixingTimePast = this._oeTimeMachine.isFixingTimePast(
          this.data.fixingDate,
          this.data.fixing.fixing,
        );
        timeState = isFixingTimePast ? ICNInputControlState.INVALID : ICNInputControlState.VALID;
      }
    }

    if (dateState !== this.data.fixingDate.state || timeState !== this.data.fixing.fixingState) {
      this.data.fixingDate.state = null;
      this.data.fixing.fixingState = null;

      return new Promise<void>((res) => {
        setImmediate(() => {
          this.data.fixingDate.state = dateState;
          this.data.fixing.fixingState = timeState;
          this.cd.markForCheck();
          res();
        });
      });
    }
    return Promise.resolve();
  }

  public validateTraderGroup(): void {
    let traderGroups: Array<CubiqTS.TradingGroupImpl> = this.getTraderGroups();

    if (this.data.traderGroup.value != null && typeof this.data.traderGroup.value === "string") {
      let code: string = (this.data.traderGroup.value as string).toLowerCase();
      let traderGroup: CubiqTS.TradingGroupImpl = traderGroups.find(
        (group: CubiqTS.TradingGroupImpl) => group.groupCode.toLowerCase() == code,
      );

      if (traderGroup != null) {
        this.data.traderGroup.value = traderGroup;
      } else if (code.trim().length == 0) {
        this.data.traderGroup.value = null;
      }
    }

    if (this.data.traderGroup.value === null || !this.data.traderGroup.traderGroupVisited) {
      this.data.traderGroup.state = ICNInputControlState.INACTIVE;
    } else {
      let valid: boolean = this.data.traderGroup.value instanceof CubiqTS.TradingGroupImpl;
      this.data.traderGroup.state = valid
        ? ICNInputControlState.VALID
        : ICNInputControlState.INVALID;
    }
    this.cd.markForCheck();
  }

  private getTraderGroups(): Array<CubiqTS.TradingGroupImpl> {
    let groups: Array<CubiqTS.TradingGroupImpl> = this.data.traderGroup.benchmarkTraderGroups;
    return groups;
  }

  /**********************************************************************/
  /* CONSTRAINT PROPAGATION */

  private propagateClientChange(): void {
    // comments
    if (
      (this.data.client.value != null &&
        this.data.previousValue.clientId !== this.data.client.value.clientId) ||
      (this.data.client.value == null && this.data.previousValue.client != null)
    ) {
      // _client is different
      if (this.data.client.value != null) {
        // client id has changed- set default
        let clientComment = this.api.getClientComment(this.refId);
        this.data.commentClient.value =
          clientComment && !this.data.commentClient.uiPrivatized
            ? clientComment
            : this.data.commentClient.uiPrivatized
              ? this.data.commentClient.privatizationLabel
              : "";
        this.data.commentPrivate.value =
          this.data.client.value.defaultPrivateComment != null &&
          !this.data.commentPrivate.uiPrivatized
            ? this.data.client.value.defaultPrivateComment
            : this.data.commentPrivate.uiPrivatized
              ? this.data.commentPrivate.privatizationLabel
              : "";
        this.onCommentPrivateChange();
        this.cd.markForCheck();
      }
    }

    ///
    this.data.previousValue.client = this.data.client.value;
    this.data.previousValue.clientId =
      this.data.previousValue.client != null ? this.data.previousValue.client.clientId : null;

    if (this.data.previousValue.client != null) {
      this.evaluateMarkupByBranch(this.data.previousValue.client.clientId);
    }
    this.cd.markForCheck();
  }

  private propagateAccountChange(): void {
    this.updateRetrieveMarkupEnabled();
  }

  public propagateClientDefaultCommentsChange(data: any): void {
    this.data.commentClient.value = this.data.commentClient.uiPrivatized
      ? this.data.commentClient.privatizationLabel
      : data.clientComment;
    this.data.commentPrivate.value = this.data.commentPrivate.uiPrivatized
      ? this.data.commentPrivate.privatizationLabel
      : data.privateComment;
    this.cd.markForCheck();
  }

  /**********************************************************************/
  /* RATE SUBSCRIPTION  */

  /**
   * Convenience method for setting the current currency pair of
   * the model. The method sets up the dependent elements in the model
   * as well.
   * @param productCode the product code (base + nonBase, or invert)
   * @returns true if the currency pair was found and setup, false otherwise
   */
  protected setCurrencyPair(productCode: string): boolean {
    this.cd.markForCheck();

    if (!this.control.isLoading && !this.data.markup.config.markupEntry) {
      this.updateMarkup(null);
      this.api.updateMarkup(this.refId, null);
    }
    this.data.currencyPair.product = productCode;

    if (this.data.previousValue.product !== productCode) {
      if (this.data.currencyPair.product != null) {
        let currencyPairLookup: CcyPairLookupService = this.currencyPairService.getBase();
        this.data.currencyPair.model = currencyPairLookup.getItem(productCode);
        this.data.currencyPair.inverted = false;
        this.data.currencyPair.base = this.data.currencyPair.product.substr(0, 3);
        this.data.currencyPair.nonBase = this.data.currencyPair.product.substr(3, 3);
        // Check if there is an invert available
        if (this.data.currencyPair.model == null) {
          this.data.currencyPair.model = currencyPairLookup.getCcyPair(
            productCode.substr(3, 3),
            productCode.substr(0, 3),
          );
          this.data.currencyPair.inverted = true;
          this.data.currencyPair.base = this.data.currencyPair.product.substr(3, 3);
          this.data.currencyPair.nonBase = this.data.currencyPair.product.substr(0, 3);
        }

        if (this.data.currencyPair.model) {
          this.data.currencyPair.fwdEnabled = this.data.currencyPair.model.allowBmarkFwdOrdersFg;
        } else {
          this.data.currencyPair.fwdEnabled = false;
        }
        this.data.currencyPair.ndfEnabled = false;

        /* RATE SUBSCRIPTION */
        let resubscribe = !(
          typeof this.data.previousValue.product === "string" &&
          this.data.previousValue.product === productCode.substr(3, 3) + productCode.substr(0, 3)
        );
        // unsubscribe
        if (resubscribe) {
          this.unsubscribeRate(this.rootRefId, this.data.previousValue.product);
          this.subscribeRate(this.rootRefId, productCode);
        } else if (this.latestRateData) {
          this.onRateUpdate(this.latestRateData);
        } else {
          logger.out.error(`no rate data on invert`);
        }

        // update previous product
        this.data.previousValue.product = this.data.currencyPair.product;
        this.data.previousValue.inverted = this.data.currencyPair.inverted;

        this.data.fixedCcy.ccyPair = productCode;
        this.data.fixedCcy.fixedCcy = this.data.currencyPair.base;
        let value = new ICFixedCcyModel(this.data.fixedCcy.fixedCcy, this.data.fixedCcy.ccyPair);
        this.data.fixedCcy.value = value;
      } else {
        this.data.currencyPair.model = null;
        this.data.currencyPair.inverted = false;
        this.data.currencyPair.base = null;
        this.data.currencyPair.nonBase = null;
        this.data.currencyPair.fwdEnabled = false;
        this.data.currencyPair.ndfEnabled = false;
        // reset marketRate
        this.unsubscribeRate(this.rootRefId, this.data.previousValue.product);
        let displayModel = new OERateDisplayModel();
        displayModel.invalidate();

        this.data.marketRate = displayModel;

        this.icCcyPair.forEach((c) => c.setMarketRate(displayModel));

        this.rate.value = new CubiqTS.BigNumber(0);

        // reset control
        this.outputExtendedLogging(
          `INFO: oe${OENEntryType[this.entryType]}Panel: [${
            this.rootRefId
          }] Resetting requestRate value`,
        );
        // update previous product
        this.data.previousValue.product = null;
        this.data.previousValue.inverted = false;
      }
      this.loadValueDateDataset();
      return true;
    } else {
      return false; // no actual change in the ccyPair
    }
  }

  /**
   * Copied in large from Spot entry
   */
  protected loadValueDateDataset(): void {
    this.cd.markForCheck();
    if (this.data && this.data.currencyPair && this.data.currencyPair.product) {
      let base: string = this.data.currencyPair.inverted
        ? this.data.currencyPair.product.substr(3, 3)
        : this.data.currencyPair.product.substr(0, 3);
      let nonBase: string = this.data.currencyPair.inverted
        ? this.data.currencyPair.product.substr(0, 3)
        : this.data.currencyPair.product.substr(3, 3);

      // const fixingStr = this.getFixingDateForCalendar();
      this.datasetService
        .getFixingCalendar(
          base,
          nonBase,
          this.data.fixingDate?.value,
          this.data.fixing?.fixing,
          this.data.currencyPair.fwdEnabled && this.fwdEnabled,
        )
        .then((calendar: CubiqTS.CurrencyPairCalendarImpl) => {
          this.data.valueDate.dataset = calendar;
          this.cd.markForCheck();
        });
    } else {
      if (this.data.valueDate.dataset != null) {
        this.data.valueDate.dataset = null;
      } else {
        this.onValueDateChange(null);
      }
    }
  }

  /**********************************************************************/
  /* MARKUP METHODS */

  public onGetMarkupCriteria($event: any): void {
    this.retrieveMarkup();
  }

  protected updateRetrieveMarkupEnabled(): void {
    if (
      this.data.account.value != null &&
      this.data.client.value != null &&
      this.data.currencyPair.model != null &&
      this.data.fixedCcy.fixedCcy != null &&
      this.data.orderType.value != null &&
      //			this.data.limitPrice.value != null &&
      this.data.orderAmount.value != null &&
      this.data.fixing.fixingCode &&
      this.data.fixing.fixingCode.code &&
      this.data.fixing.fixing &&
      this.data.fixing.fixing.value &&
      this.data.fixing.fixing.timezoneCd &&
      (!this.isBulking || (this.isBulking && this.isBulked))
    ) {
      this.data.markup.retrieveEnabled = true;
    } else {
      this.data.markup.retrieveEnabled = false;
    }
    this.cd.markForCheck();
  }

  protected retrieveMarkup() {
    let criteria = this.forwardAdapter.createMarkupCriteria(this.getter.get(this.refId));

    setTimeout(() => {
      this.data.markup.criteria = criteria;
      this.markupInputControl.forEach((c) => c.markupCriteriaUpdate(this.data.markup.criteria));
      this.cd.markForCheck();
    }, 0);
    this.cd.markForCheck();
  }

  protected updateMarkup(markup: ICMarkupModel) {
    if (this.markupInputControl) {
      if (markup == null) {
        this.markupInputControl.forEach((c) => c.writeValue(null));
      } else if (this.data.markup.value !== Number(markup.value)) {
        this.markupInputControl.forEach((c) => c.load(markup));
      }
    }
  }

  /**********************************************************************/
  /* UTILITY  */

  protected checkMarkupDefault(type: CubiqTS.RequestOrderType): boolean {
    switch (type) {
      case CubiqTS.RequestOrderType.TP:
      case CubiqTS.RequestOrderType.SL_ATRATE:
      case CubiqTS.RequestOrderType.SL_ASK:
      case CubiqTS.RequestOrderType.SL_BID:
      case CubiqTS.RequestOrderType.TSL:
      case CubiqTS.RequestOrderType.TSL_ASK:
      case CubiqTS.RequestOrderType.TSL_BID:
      case CubiqTS.RequestOrderType.MARKET:
        return true;
      default:
        return false;
    }
  }

  /**********************************************************************/
  /* HELPERS - Debug Log - Extended logging  */

  private outputExtendedLogging(value: string, dump: any = null): void {
    this.oeLogger.outputDevLogToConsole(value, false, dump);
  }

  private outputRateLogging(value: string, dump: any = null): void {
    this.oeLogger.outputRateLogToConsole(value, false, dump);
  }
  /**********************************************************************/
  /* RATE SUBSCRIPTION  */
  protected unsubscribeRate(rootRefId: string, productCode: string) {
    this.latestRateData = null;
    this.rate.value = new CubiqTS.BigNumber(0);

    if (this.rateSubscription) {
      this.rateSubscription.unsubscribe();
      this.rateSubscription = null;
    }

    if (typeof productCode === "string" && productCode.length == 6) {
      this.outputExtendedLogging(
        `INFO: oe${OENEntryType[this.entryType]}Panel: [${
          this.rootRefId
        }] requesting Rate UNSUBSCRIBE for ${productCode} ${
          this.rootRefId != rootRefId ? " for (" + rootRefId + ")" : ""
        }`,
      );
      this.api.rateUnsubscribe(rootRefId, this.entryType, productCode);
    }
  }

  protected subscribeRate(rootRefId: string, productCode: string) {
    if (typeof productCode === "string" && productCode.length == 6) {
      this.outputExtendedLogging(
        `INFO: oe${OENEntryType[this.entryType]}Panel: [${
          this.rootRefId
        }] requesting Rate SUBSCRIBE for ${productCode} ${
          this.rootRefId != rootRefId ? " for (" + rootRefId + ")" : ""
        }`,
      );
      let subscription = this.api.rateSubscribe(rootRefId, this.entryType, productCode);

      if (subscription != null && this.rateSubscription == null) {
        this.rateSubscription = subscription.subscribe(this.onRateUpdate.bind(this));
      } else if (subscription == null) {
        logger.out.error(
          "ERROR: oe${OENEntryType[this.entryType]}Entry.subscribeRate(): Rate subscription failed!",
        );
      }
    }
  }

  protected onRateUpdate(rate: any): void {
    this.cd.markForCheck();
    this.latestRateData = rate; // temp

    if (
      rate instanceof OERateTransportModel &&
      rate.refIds instanceof Array &&
      rate.value instanceof OERateDataModel
    ) {
      if (rate.refIds.includes(this.rootRefId)) {
        let rateData = rate.value;
        if (
          rateData.ccyPair === this.data.currencyPair.product ||
          rateData.invertedCcyPair === this.data.currencyPair.product
        ) {
          let displayModel =
            rateData.invertedCcyPair === this.data.currencyPair.product
              ? rateData.invertedDisplayModel
              : rateData.displayModel;
          let rateValue =
            rateData.invertedCcyPair === this.data.currencyPair.product
              ? rateData.invertedRateValue
              : rateData.rateValue;
          this.outputRateLogging(
            `INFO: oe${OENEntryType[this.entryType]}Panel: [${
              this.rootRefId
            }] Received rate ${rateValue.toFixed(4)} for ${this.data.currencyPair.product} (${
              rateData.subscrKey
            })`,
          );

          if (
            displayModel instanceof OERateDisplayModel &&
            rateValue instanceof CubiqTS.BigNumber
          ) {
            // allocate display model
            this.data.marketRate = displayModel;
            this.icCcyPair.forEach((c) => c.setMarketRate(displayModel));
          } else {
            logger.out.error(
              `ERROR: oe${
                OENEntryType[this.entryType]
              }Entry.onRateUpdate(): Invalid Rate in Rate update`,
            );
          }
        } else if (this.data.currencyPair.product !== null) {
          logger.out.error(
            `ERROR: oe${
              OENEntryType[this.entryType]
            }Entry.onRateUpdate(): 'Invalid ccyPair in Rate update`,
          );
        }
      }
    } else {
      logger.out.error(
        `ERROR: oe${
          OENEntryType[this.entryType]
        }Entry.onRateUpdate(): Invalid Rate Transport model`,
      );
    }
  }

  public updateOBOClient(oboClient: string): void {
    this.data.oboClient.externalState = null;
    this.api.updateOBOClient(this.refId, oboClient);
  }

  public updateOBOAccount(oboAccount: string): void {
    this.data.oboAccount.externalState = null;
    this.api.updateOBOAccount(this.refId, oboAccount);
  }

  protected isMarkupEnabled(client: CubiqTS.ClientImpl): boolean {
    let applyMarkup: boolean = this.isBranchMarkupEnabled(client);
    return applyMarkup;
  }

  private isBranchMarkupEnabled(client: CubiqTS.ClientImpl): boolean {
    if (client != null) {
      // check if client is sey
      let branchCode = client != null ? client.branchCd : "";
      let branch = this.branchLookupService.getItem(branchCode);
      if (branch instanceof CubiqTS.BranchImpl && branch.applyMarkupFg) {
        // check if client branch is mkt allowed
        return true;
      }
    }
    return false;
  }

  private evaluateMarkupByBranch(clientStr: string): void {
    let client: CubiqTS.ClientImpl = this.clientLookupService.getItem(clientStr);
    let previousMarkupDisabledFg: boolean = this.data.markup.disabled;
    let markupEnabled: boolean = this.isMarkupEnabled(client);

    if (!previousMarkupDisabledFg && !markupEnabled) {
      this.propagateMarkupDefaultData();
      this.api.updateMarkup(this.refId, null);
    }
    this.data.markup.disabled = !markupEnabled;
    this.cd.markForCheck();
  }
}
